import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useQuoteBuilderNav } from '../../contexts/QuoteBuilderNavContext';
import { quotesService } from '../../services/quotesService';
import { clientsService } from '../../services/clientsService';
import { uploadService } from '../../services/uploadService';
import { itinerariesService } from '../../services/itinerariesService';
import { UserSearch, Calendar, Users, Check, ChevronRight, ChevronDown, ChevronUp, Plus, AlertCircle } from 'lucide-react';
import { useToast } from '../common/Toast';
import Modal from '../common/Modal';
import './QuoteBuilder.css';
import './components/ComponentForms.css';

// Component type registry (categories/types, form-per-type switch, and the
// componentData->saved-component builder) - shared with EditItineraryModal.jsx
// so quotes and itineraries edit components identically. See
// componentFormRegistry.js for why this is centralized here.
import {
  componentCategories,
  componentTypes,
  componentTypeDefaults,
  inferComponentType,
  getComponentFormInitialData,
  renderComponentTypeForm,
  buildSavedComponent
} from './componentFormRegistry';
import ClientModal from './ClientModal';
import { getComponentOptions, hasMultipleOptions, blankAdditionalOption, getOptionTotal } from './optionUtils';
import ComponentLibraryModal from './Library/ComponentLibraryModal';
import PDFQuoteUploadModal from './PDFQuoteUploadModal';
import PDFQuoteReviewModal from './PDFQuoteReviewModal';
import ConversationalQuoteBuilder from './ConversationalQuoteBuilder';
import QuoteRenderer from '../templates/QuoteRenderer';
import QuoteOptionTabs from './QuoteOptionTabs';
import { dateOnly } from '../../utils/apiDate.mjs';

// Step metadata for the persistent header/footer bars (Step X of 4, progress
// bar/dots) - mirrors the Figma "Voyager Pro" design's STEPS array. Kept as
// simple ids matching the existing `activeStep` state values so nothing about
// the actual step-switching logic below has to change.
const QUOTE_BUILDER_STEPS = [
  { id: 'client', number: 1, title: 'Select Client', description: 'Choose or create a client' },
  { id: 'details', number: 2, title: 'Trip Details', description: 'Dates & travelers' },
  { id: 'components', number: 3, title: 'Components', description: 'Hotels, flights & more' },
  { id: 'preview', number: 4, title: 'Review & Send', description: 'Finalize and deliver' },
];

// Reconstruct the `travelers.children` array (each `{ age }`) from a saved
// quote row. Ages live in `children_ages` (a JSON array, e.g. "[5, 12]"),
// kept separate from the `children` count column. Falls back to defaulting
// every child to age 5 if `children_ages` is missing/malformed/short (e.g.
// quotes saved before this column was wired up), rather than silently
// showing "0yo" for every child.
const parseChildrenAges = (quoteRow) => {
  const count = quoteRow?.children || 0;
  let ages = [];
  if (quoteRow?.children_ages) {
    try {
      const parsed = typeof quoteRow.children_ages === 'string' ? JSON.parse(quoteRow.children_ages) : quoteRow.children_ages;
      if (Array.isArray(parsed)) ages = parsed;
    } catch (e) {
      console.error('Invalid children_ages JSON:', quoteRow.children_ages);
    }
  }
  return Array.from({ length: count }, (_, i) => ({ age: Number.isFinite(ages[i]) ? ages[i] : 5 }));
};

const QuoteBuilder = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Shared with independent agents via /independent/quotes/new|:id/edit (see
  // App.js) - same quotesService/clientsService (unified, role-scoped
  // server-side), just a different URL prefix for navigation targets.
  const basePath = user?.role === 'independent_agent' ? '/independent' : '/agent';
  const toast = useToast();
  const { setNavState } = useQuoteBuilderNav();
  const { id: urlId } = useParams(); // Get quote ID from URL for editing
  const [quoteId, setQuoteId] = useState(urlId); // State-based quote ID
  const isEditMode = !!quoteId; // Reactive edit mode based on state
  // Whether this page load was a genuine "open an existing quote to edit"
  // (or a lead-conversion landing, which also arrives with a real :id in
  // the URL) vs. a brand-new quote (route "quotes/new", no urlId) that
  // later acquires a quoteId mid-session via autosave/save-draft. Captured
  // once at mount - quoteId itself changes over time (autosave/handleSave
  // assign it as soon as the draft is first persisted), which would
  // otherwise make fetchQuote()'s one-time "which step should this land
  // on" decision re-run and yank the agent to Review & Send the moment
  // the very first autosave fires after just picking a client, before
  // they've touched Trip Details/Components at all.
  const isEditModeFromUrlRef = useRef(!!urlId);

  // Quote state
  const [quote, setQuote] = useState({
    quoteTitle: '',
    client: null,
    travelDates: {
      start: '',
      end: ''
    },
    travelers: {
      adults: 1,
      children: []
    },
    components: [],
    options: [], // For package comparisons
    totalPrice: 0,
    depositAmount: 0,
    depositDueDate: '',
    expiryDate: '', // Quote validity/expiration date (backed by quotes.expires_at)
    notes: '',
    status: 'draft',
    statusHistory: [], // [{status, timestamp, note}] - see quotes.status_history (Phase E)
    agentInfo: {
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      agencyName: user?.agencyName || ''
    }
  });

  // UI state
  const [activeStep, setActiveStep] = useState('client'); // client, details, components, preview
  const [showClientModal, setShowClientModal] = useState(false);
  const [showComponentModal, setShowComponentModal] = useState(false);
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [showPDFUploadModal, setShowPDFUploadModal] = useState(false);
  const [showPDFReviewModal, setShowPDFReviewModal] = useState(false);
  const [showConversationalBuilder, setShowConversationalBuilder] = useState(false);
  const [pdfExtractedData, setPdfExtractedData] = useState(null);
  const [selectedComponentType, setSelectedComponentType] = useState('');
  const [editingComponent, setEditingComponent] = useState(null);
  const [expandedOptionId, setExpandedOptionId] = useState(null);
  const [depositManuallyOverridden, setDepositManuallyOverridden] = useState(false);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState('modern'); // Template/Theme selection
  // Pure UI state for the redesigned Step 1 (inline client search) and
  // Step 3 (category-tabbed component picker) - doesn't touch the quote
  // data model, just what's visible while browsing.
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [activeComponentCategory, setActiveComponentCategory] = useState(componentCategories[0]?.id);

  // Multi-option AI quote comparison state
  // quoteOptionGroup holds one entry per sibling option: { id, optionName, data }
  // `data` is the full builder-shaped quote object for that option, kept in sync
  // whenever the agent switches tabs so edits aren't lost.
  const [quoteOptionGroup, setQuoteOptionGroup] = useState([]);
  const [activeOptionIndex, setActiveOptionIndex] = useState(0);
  const [showSendModal, setShowSendModal] = useState(false);

  // Autosave: 'idle' (nothing to save yet) | 'unsaved' (edits pending,
  // debounce running) | 'saving' | 'saved' | 'error'. Scoped to the single-
  // quote case only (not the multi-option AI comparison group) - that flow
  // already has its own more involved parallel-save logic in
  // handleSaveQuote and is out of scope here.
  const [saveStatus, setSaveStatus] = useState('idle');
  const autosaveTimerRef = useRef(null);
  // Guards against autosaving immediately after a fresh load/hydration
  // (fetchQuote, AI quote generation) mistaking that for a user edit.
  const skipNextAutosaveRef = useRef(true);
  // Gates the very first autosave on a brand-new quote until the agent has
  // actually completed Trip Details (title + dates) and clicked through to
  // Components - not just picked a client. Picking a client alone used to
  // be enough to trigger a save, which persisted a near-empty draft and is
  // what surfaced the title-coalesced-to-null bug (see commit 73eb81b).
  // Once true it stays true for the rest of the session - this only exists
  // to delay the *first* save, not to re-gate every future edit. Any path
  // that hydrates a quote which already exists in the DB (opening an
  // existing quote, a lead conversion, an AI-generated quote) sets this
  // immediately, since Trip Details necessarily already happened for that
  // record.
  const detailsStepCompletedRef = useRef(!!urlId);

  // Load clients on mount
  useEffect(() => {
    fetchClients();
    if (isEditMode) {
      fetchQuote();
    }
  }, [quoteId, isEditMode]);

  const fetchClients = async () => {
    try {
      const clients = await clientsService.getAll();
      setClients(clients);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setErrors({ fetch: 'Failed to load clients' });
    }
  };

  const fetchQuote = async () => {
    try {
      setLoadingQuote(true);
      console.log('Fetching quote with ID:', quoteId); // Debug log
      const quoteData = await quotesService.getById(quoteId);
      
      console.log('Loaded quote data:', quoteData); // Debug log
      
      // Check if quote has been converted to an itinerary
      if (quoteData.status === 'converted') {
        console.log('Quote has been converted, finding corresponding itinerary...');
        try {
          const itinerary = await itinerariesService.findByQuoteId(quoteId);
          console.log('Found itinerary:', itinerary);
          toast.info('This quote has been converted to an itinerary. Redirecting you to the itinerary...');
          navigate(`${basePath}/itineraries`);
          return;
        } catch (itineraryError) {
          console.error('Error finding itinerary:', itineraryError);
          toast.warning('This quote has been converted to an itinerary, but we could not find the itinerary. Redirecting to itineraries list...');
          navigate(`${basePath}/itineraries`);
          return;
        }
      }
      
      // Parse JSON strings if they come as strings
      let componentsArray = [];
      let optionsArray = [];
      
      console.log('Raw components from DB:', quoteData.components);
      console.log('Components type:', typeof quoteData.components);
      
      try {
        // Handle components - check for null, string, or array
        if (quoteData.components === null || quoteData.components === undefined) {
          componentsArray = [];
          console.log('Components is null/undefined, using empty array');
        } else if (typeof quoteData.components === 'string') {
          componentsArray = JSON.parse(quoteData.components) || [];
          console.log('Parsed components array:', componentsArray);
          console.log('Components count after parsing:', componentsArray.length);
        } else if (Array.isArray(quoteData.components)) {
          componentsArray = quoteData.components;
          console.log('Components already array:', componentsArray.length);
        } else {
          console.warn('Unexpected components type:', typeof quoteData.components, quoteData.components);
          componentsArray = [];
        }
        
        // Handle options - check for null, string, or array
        if (quoteData.options === null || quoteData.options === undefined) {
          optionsArray = [];
        } else if (typeof quoteData.options === 'string') {
          optionsArray = JSON.parse(quoteData.options) || [];
        } else if (Array.isArray(quoteData.options)) {
          optionsArray = quoteData.options;
        } else {
          optionsArray = [];
        }
      } catch (parseError) {
        console.error('Error parsing JSON data:', parseError);
        console.error('Failed to parse components:', quoteData.components);
        // Set defaults if parsing fails
        componentsArray = [];
        optionsArray = [];
      }

      // Parse status_history (see quotes.status_history / appendQuoteStatusHistory) -
      // best-effort, older quotes may have it null/invalid.
      let statusHistoryArray = [];
      try {
        if (typeof quoteData.status_history === 'string') {
          statusHistoryArray = JSON.parse(quoteData.status_history) || [];
        } else if (Array.isArray(quoteData.status_history)) {
          statusHistoryArray = quoteData.status_history;
        }
        if (!Array.isArray(statusHistoryArray)) statusHistoryArray = [];
      } catch (e) {
        statusHistoryArray = [];
      }
      
      // Transform the quote data from API to component format
      const transformedQuote = {
        id: quoteData.id || quoteId, // Include the quote ID for preview functionality
        quoteTitle: quoteData.title || '',
        client: {
          id: quoteData.client_id,
          firstName: quoteData.client_name?.split(' ')[0] || '',
          lastName: quoteData.client_name?.split(' ').slice(1).join(' ') || '',
          email: quoteData.client_email || '',
          phone: quoteData.client_phone || ''
        },
        travelDates: {
          start: quoteData.travel_start_date ? quoteData.travel_start_date.split('T')[0] : '',
          end: quoteData.travel_end_date ? quoteData.travel_end_date.split('T')[0] : ''
        },
        travelers: {
          adults: quoteData.adults || 1,
          children: parseChildrenAges(quoteData)
        },
        components: componentsArray,
        options: optionsArray,
        totalPrice: parseFloat(quoteData.total_amount) || 0,
        depositAmount: parseFloat(quoteData.deposit_amount) || 0,
        depositDueDate: quoteData.deposit_due_date ? quoteData.deposit_due_date.split('T')[0] : '',
        expiryDate: dateOnly(quoteData.expires_at),
        notes: quoteData.notes || '',
        status: quoteData.status || 'draft',
        statusHistory: statusHistoryArray,
        agentInfo: {
          name: user?.name || '',
          email: user?.email || '',
          phone: user?.phone || '',
          agencyName: user?.agencyName || '',
          termsConditions: quoteData?.terms_conditions || ''
        }
      };
      
      console.log('Final transformed quote components:', transformedQuote.components);
      console.log('Components in transformed quote:', transformedQuote.components.length);
      
      skipNextAutosaveRef.current = true;
      // This quote already exists in the DB, so Trip Details already
      // happened for it at some point - future edits should autosave
      // normally regardless of which step the agent lands on below.
      detailsStepCompletedRef.current = true;
      setQuote(transformedQuote);
      
      // Check if deposit was manually overridden (differs from auto-calculated)
      const loadedDeposit = parseFloat(quoteData.deposit_amount) || 0;
      const calculatedDeposit = calculateTotalDeposit(componentsArray);
      if (Math.abs(loadedDeposit - calculatedDeposit) > 0.01) {
        console.log('🔒 Detected manual deposit override:', { loaded: loadedDeposit, calculated: calculatedDeposit });
        setDepositManuallyOverridden(true);
      }
      
      // Set the selected template from the database
      if (quoteData.template_id) {
        setSelectedTemplate(quoteData.template_id);
        console.log('Loaded template:', quoteData.template_id);
      }
      
      // If we have data, show the preview step instead of client step. Only
      // do this on a genuine "open an existing quote" load (real :id in the
      // URL from the start, e.g. clicking a quote in QuotesList, or a lead
      // conversion landing) - NOT when this fetchQuote() call was triggered
      // by quoteId being assigned mid-session (autosave or Save Draft on a
      // brand-new "quotes/new" quote), which would otherwise yank the agent
      // straight to Review & Send the moment the client is selected and the
      // very first autosave fires, before Trip Details/Components are ever
      // shown.
      if (isEditModeFromUrlRef.current && transformedQuote.client && transformedQuote.client.id) {
        if (location.state?.fromLeadConversion) {
          // Coming straight from "Convert to Quote" on a lead: land on the
          // Components step instead of jumping all the way to Review & Send
          // so the agent actually sees what was pre-filled (client, dates,
          // and the auto-built component) before deciding it's ready to
          // send, rather than skipping 3 of the 4 steps silently.
          setActiveStep('components');

          const summaryParts = [];
          const clientName = [transformedQuote.client.firstName, transformedQuote.client.lastName]
            .filter(Boolean)
            .join(' ');
          if (clientName) summaryParts.push(`client ${clientName}`);
          if (transformedQuote.travelDates.start) summaryParts.push('travel dates');
          const partySize = transformedQuote.travelers.adults + transformedQuote.travelers.children.length;
          summaryParts.push(`${partySize} traveler${partySize === 1 ? '' : 's'}`);
          const firstComponent = componentsArray[0];
          if (firstComponent) {
            const typeLabel = componentTypes.find((t) => t.id === firstComponent.type)?.name || firstComponent.type;
            summaryParts.push(`a ${typeLabel} component`);
          }

          toast.info(
            `Quote created from lead — ${summaryParts.join(', ')} were pre-filled from the conversation. Review the details below before sending.`,
            { duration: 8000 }
          );
        } else {
          setActiveStep('preview');
        }
      }
    } catch (error) {
      console.error('Error fetching quote:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      setErrors({ fetch: 'Failed to load quote' });
      toast.error('Failed to load quote. Please try again.');
      navigate(`${basePath}/quotes`);
    } finally {
      setLoadingQuote(false);
    }
  };

  // Calculate total price when components change
  useEffect(() => {
    try {
      // Make sure components and options are arrays
      const components = Array.isArray(quote.components) ? quote.components : [];
      const options = Array.isArray(quote.options) ? quote.options : [];
      
      // Calculate total from components using new function that respects bundled components
      const total = calculateTotalPrice(components);
      
      // Calculate total from options
      const optionsTotal = options.reduce((sum, option) => {
        // Make sure option.components is an array
        const optComponents = Array.isArray(option.components) ? option.components : [];
        
        const optionSum = optComponents.reduce((optSum, comp) => {
          const price = parseFloat(comp.price) || 0;
          return optSum + price;
        }, 0);
        
        return sum + optionSum;
      }, 0);
      
      // Calculate total deposit from components
      const totalDeposit = calculateTotalDeposit(components);
      console.log('💰 useEffect updating quote - totalPrice:', total + optionsTotal, 'depositAmount:', totalDeposit, 'manualOverride:', depositManuallyOverridden);
      
      // Update quote with calculated total and deposit (only update deposit if not manually overridden)
      setQuote(prev => ({ 
        ...prev, 
        totalPrice: total + optionsTotal,
        depositAmount: depositManuallyOverridden ? prev.depositAmount : totalDeposit 
      }));
    } catch (error) {
      console.error('Error calculating total price:', error);
      // Keep existing total price if calculation fails
    }
  }, [quote.components, quote.options, depositManuallyOverridden]);

  // Handle client selection. Deliberately does NOT auto-advance to the next
  // step (it used to) - selecting just highlights the client, matching the
  // Figma "Voyager Pro" design's select-then-Continue pattern now that the
  // step footer's Continue button is always visible/persistent.
  const handleClientSelect = (client) => {
    // Normalize client data structure
    const normalizedClient = {
      ...client,
      firstName: client.firstName || client.first_name,
      lastName: client.lastName || client.last_name
    };
    setQuote(prev => ({ ...prev, client: normalizedClient }));
    setShowClientModal(false);
  };

  // Handle new client creation
  const handleNewClient = async (clientData) => {
    try {
      setLoading(true);
      const response = await clientsService.create(clientData);
      
      // Backend now returns full client object instead of just {clientId, message}
      // Normalize the client data to ensure consistent property names
      const newClient = {
        id: response.id || response.clientId,
        firstName: response.first_name || response.firstName,
        lastName: response.last_name || response.lastName,
        email: response.email,
        phone: response.phone,
        first_name: response.first_name,
        last_name: response.last_name,
        ...response
      };
      
      setClients(prev => [...prev, newClient]);
      handleClientSelect(newClient);
    } catch (error) {
      console.error('Error creating client:', error);
      setErrors({ client: 'Failed to create client' });
    } finally {
      setLoading(false);
    }
  };

  // Transform a raw database quote record (from the AI Quote Builder finalize
  // endpoint) into the shape used by the builder's `quote` state.
  const transformAIQuoteToBuilderShape = (finalizedQuoteData, isMultiOption, optionName) => {
    // Parse JSON strings if they come as strings (from database)
    let componentsArray = [];
    let optionsArray = [];

    try {
      if (finalizedQuoteData.components === null || finalizedQuoteData.components === undefined) {
        componentsArray = [];
      } else if (typeof finalizedQuoteData.components === 'string') {
        componentsArray = JSON.parse(finalizedQuoteData.components) || [];
      } else if (Array.isArray(finalizedQuoteData.components)) {
        componentsArray = finalizedQuoteData.components;
      }

      if (finalizedQuoteData.options === null || finalizedQuoteData.options === undefined) {
        optionsArray = [];
      } else if (typeof finalizedQuoteData.options === 'string') {
        optionsArray = JSON.parse(finalizedQuoteData.options) || [];
      } else if (Array.isArray(finalizedQuoteData.options)) {
        optionsArray = finalizedQuoteData.options;
      }
    } catch (parseError) {
      console.error('Error parsing AI quote components:', parseError);
      componentsArray = [];
      optionsArray = [];
    }

    const baseTitle = finalizedQuoteData.title || finalizedQuoteData.trip_name || '';
    const displayTitle = isMultiOption && optionName ? `${baseTitle} - ${optionName}` : baseTitle;

    return {
      id: finalizedQuoteData.id,
      quoteTitle: displayTitle,
      client: {
        id: finalizedQuoteData.client_id,
        firstName: finalizedQuoteData.client_first_name || '',
        lastName: finalizedQuoteData.client_last_name || '',
        email: finalizedQuoteData.client_email || '',
        phone: finalizedQuoteData.client_phone || ''
      },
      travelDates: {
        start: finalizedQuoteData.travel_start_date ? finalizedQuoteData.travel_start_date.split('T')[0] :
               finalizedQuoteData.start_date ? finalizedQuoteData.start_date.split('T')[0] : '',
        end: finalizedQuoteData.travel_end_date ? finalizedQuoteData.travel_end_date.split('T')[0] :
             finalizedQuoteData.end_date ? finalizedQuoteData.end_date.split('T')[0] : ''
      },
      travelers: {
        adults: finalizedQuoteData.adults || 1,
        children: parseChildrenAges(finalizedQuoteData)
      },
      components: componentsArray,
      options: optionsArray,
      totalPrice: parseFloat(finalizedQuoteData.total_amount || finalizedQuoteData.total_price || 0),
      depositAmount: parseFloat(finalizedQuoteData.deposit_amount || 0),
      depositDueDate: finalizedQuoteData.deposit_due_date ? finalizedQuoteData.deposit_due_date.split('T')[0] : '',
      notes: finalizedQuoteData.notes || '',
      status: finalizedQuoteData.status || 'draft',
      agentInfo: {
        name: user?.name || '',
        email: user?.email || '',
        phone: user?.phone || '',
        agencyName: user?.agencyName || '',
        termsConditions: finalizedQuoteData?.terms_conditions || ''
      }
    };
  };

  // Handle AI Quote Builder finalization
  // `finalizedQuoteData` is either a single database quote record, or (for
  // multi-option "Good/Better/Best" quotes) an array of sibling quote records
  // that share a parent_quote_id.
  const handleAIQuoteFinalized = (finalizedQuoteData) => {
    console.log('AI Quote finalized, hydrating quote builder:', finalizedQuoteData);

    if (Array.isArray(finalizedQuoteData)) {
      // Multi-option quote: build the comparison group and load the first option
      const group = finalizedQuoteData.map((rawQuote) => {
        const optionName = rawQuote.option_name || null;
        const transformed = transformAIQuoteToBuilderShape(rawQuote, true, optionName);
        return {
          id: rawQuote.id,
          optionName: optionName || 'Option',
          data: transformed
        };
      });

      setQuoteOptionGroup(group);
      setActiveOptionIndex(0);
      setQuoteId(group[0].id);
      setQuote(group[0].data);

      console.log(`Quote builder hydrated with ${group.length} multi-option AI quotes:`, group.map(g => g.optionName));
    } else {
      // Single quote (existing behavior)
      setQuoteOptionGroup([]);
      setActiveOptionIndex(0);

      const transformedQuote = transformAIQuoteToBuilderShape(finalizedQuoteData, false, null);
      setQuoteId(finalizedQuoteData.id);
      skipNextAutosaveRef.current = true;
      // The AI Quote Builder already created a real, persisted quote record
      // with Trip Details filled in - further edits here should autosave
      // normally.
      detailsStepCompletedRef.current = true;
      setQuote(transformedQuote);

      console.log('Quote builder hydrated with single AI-generated quote:', transformedQuote);
    }

    // Close the conversational builder modal
    setShowConversationalBuilder(false);

    // Transition to preview step to show the populated quote
    setActiveStep('preview');
  };

  // Switch the active tab in a multi-option quote comparison.
  // Persists any in-progress edits on the currently active option into
  // quoteOptionGroup before loading the target option's data into `quote`,
  // so nothing the agent typed/changed is lost when going back and forth.
  const handleSwitchOption = (newIndex) => {
    if (newIndex === activeOptionIndex) return;

    setQuoteOptionGroup(prevGroup => {
      const updatedGroup = prevGroup.map((option, idx) =>
        idx === activeOptionIndex ? { ...option, data: quote } : option
      );

      const target = updatedGroup[newIndex];
      if (target) {
        setQuote(target.data);
        setQuoteId(target.id);
      }

      return updatedGroup;
    });

    setActiveOptionIndex(newIndex);
  };

  // Calculate total price from all components (excluding bundled components)
  const calculateTotalPrice = (components) => {
    console.log('🔍 calculateTotalPrice called with components:', components);
    const result = components.reduce((total, component) => {
      const isOptional = component.isOption === true || component.data?.isOption === true;
      const paymentTiming = component.paymentTiming || component.data?.paymentTiming;
      
      // For optional components with bundled timing, don't add to total price
      if (isOptional && paymentTiming === 'bundled') {
        console.log(`Component ${component.title}: bundled, not adding to total price`);
        return total;
      }
      
      // For all other components, add the price
      const price = parseFloat(component.price) || 0;
      console.log(`Component ${component.title}: adding $${price} to total price`);
      return total + price;
    }, 0);
    console.log('🔍 calculateTotalPrice result:', result);
    return result;
  };

  // Calculate total deposit from all components
  const calculateTotalDeposit = (components) => {
    console.log('🔍 calculateTotalDeposit called with components:', components);
    const result = components.reduce((total, component) => {
      let depositAmount = 0;
      const isOptional = component.isOption === true || component.data?.isOption === true;
      
      // For optional components, check paymentTiming field first (check both top-level and data-level)
      const paymentTiming = component.paymentTiming || component.data?.paymentTiming;
      if (isOptional && paymentTiming) {
        if (paymentTiming === 'immediate') {
          // Pay full price when selected
          depositAmount = parseFloat(component.price) || 0;
        } else if (paymentTiming === 'with_deposit') {
          // Pay deposit amount when selected
          depositAmount = parseFloat(component.optionalDepositAmount || component.data?.optionalDepositAmount) || 0;
        } else if (paymentTiming === 'with_final') {
          // Pay with final payment - nothing due initially ($0)
          depositAmount = 0;
        } else if (paymentTiming === 'bundled') {
          // Already included in package - no additional payment ($0)
          depositAmount = 0;
        }
      } else {
        // For required components or optional components without paymentTiming, use existing logic
        // Check for deposit in component.depositAmount (Package Deal components)
        if (component.depositRequired && component.depositAmount) {
          depositAmount = parseFloat(component.depositAmount) || 0;
        }
        // Check for deposit in component.data.deposit_due (Disney components)
        else if (component.data && component.data.deposit_due) {
          depositAmount = parseFloat(component.data.deposit_due) || 0;
        }
      }
      
      console.log(`Component ${component.title}: isOptional=${isOptional}, paymentTiming=${paymentTiming}, depositRequired=${component.depositRequired}, depositAmount=${component.depositAmount}, data.deposit_due=${component.data?.deposit_due}, final=${depositAmount}`);
      return total + depositAmount;
    }, 0);
    console.log('🔍 calculateTotalDeposit result:', result);
    return result;
  };

  // Validate deposit amounts
  const validateDeposits = (components, totalPrice) => {
    const errors = [];
    let totalDeposits = 0;

    // Add safety check to ensure components is an array
    const safeComponents = Array.isArray(components) ? components : [];

    safeComponents.forEach((component, index) => {
      if (component.depositRequired) {
        const depositAmount = parseFloat(component.depositAmount) || 0;
        const componentPrice = parseFloat(component.price) || 0;
        
        if (depositAmount <= 0) {
          errors.push(`Component "${component.title}" requires a deposit amount greater than $0`);
        }
        
        if (depositAmount > componentPrice) {
          errors.push(`Component "${component.title}" deposit ($${depositAmount.toFixed(2)}) cannot exceed component price ($${componentPrice.toFixed(2)})`);
        }
        
        totalDeposits += depositAmount;
      }
    });

    if (totalDeposits > totalPrice) {
      errors.push(`Total deposits ($${totalDeposits.toFixed(2)}) cannot exceed total quote price ($${totalPrice.toFixed(2)})`);
    }

    return errors;
  };

  // Helper function to clear client selection data when quote is modified
  const clearClientSelections = () => {
    setQuote(prev => ({
      ...prev,
      options: {
        ...prev.options,
        client_selections: undefined,
        selected_total: undefined,
        selected_deposit: undefined,
        selection_timestamp: undefined
      }
    }));
  };

  // Handle component addition/edit - shared with EditItineraryModal.jsx via
  // buildSavedComponent() (see componentFormRegistry.js) so both editors
  // produce identically-shaped components.
  const handleAddComponent = (componentData) => {
    console.log('QuoteBuilder handleAddComponent called with:', componentData);

    const savedComponent = buildSavedComponent({ componentData, selectedComponentType, editingComponent });

    setQuote(prev => {
      let updatedComponents;
      if (editingComponent) {
        const oldPrice = parseFloat(editingComponent.price) || 0;
        const newPrice = savedComponent.price;
        const priceDifference = newPrice - oldPrice;
        updatedComponents = prev.components.map(comp =>
          comp.id === editingComponent.id ? savedComponent : comp
        );
        return {
          ...prev,
          components: updatedComponents,
          totalPrice: prev.totalPrice + priceDifference,
          depositAmount: calculateTotalDeposit(updatedComponents),
          options: {
            ...prev.options,
            client_selections: undefined,
            selected_total: undefined,
            selected_deposit: undefined,
            selection_timestamp: undefined
          }
        };
      }

      updatedComponents = [...prev.components, savedComponent];
      return {
        ...prev,
        components: updatedComponents,
        totalPrice: prev.totalPrice + savedComponent.price,
        depositAmount: calculateTotalDeposit(updatedComponents),
        options: {
          ...prev.options,
          client_selections: undefined,
          selected_total: undefined,
          selected_deposit: undefined,
          selection_timestamp: undefined
        }
      };
    });

    // Close modal and reset state
    console.log('Component saved successfully, closing modal');
    setShowComponentModal(false);
    setSelectedComponentType('');
    setEditingComponent(null);

    // Auto-save if editing existing quote
    if (isEditMode) {
      // Will be saved when user clicks save
    }
  };

  // Handle template selection from Component Library
  const handleSelectTemplate = (template) => {
    const componentData = {
      type: template.id,
      title: template.name,
      description: template.description,
      price: 0, // Will be set in the form
      data: template.template_data
    };
    
    setSelectedComponentType(template.id);
    setEditingComponent({ data: template.template_data });
    setShowLibraryModal(false);
    setShowComponentModal(true);
  };

  // Handle component deletion
  const handleDeleteComponent = (componentId) => {
    setQuote(prev => {
      const componentToDelete = prev.components.find(comp => comp.id === componentId);
      const updatedComponents = prev.components.filter(comp => comp.id !== componentId);
      const totalDeposit = calculateTotalDeposit(updatedComponents);
      const priceToSubtract = parseFloat(componentToDelete?.price) || 0;
      
      return {
        ...prev,
        components: updatedComponents,
        totalPrice: prev.totalPrice - priceToSubtract,
        depositAmount: totalDeposit,
        options: {
          ...prev.options,
          client_selections: undefined,
          selected_total: undefined,
          selected_deposit: undefined,
          selection_timestamp: undefined
        }
      };
    });
  };

  // ─── Multi-option comparison (Quote Builder Phase 2) ─────────────────────
  // A component's `options[]` array is purely additive - see optionUtils.js.
  // options[0] always mirrors the primary form's own fields (kept in sync by
  // buildSavedComponent whenever that form is saved); these three handlers
  // only manage options[1+], the extra agent-added alternatives a client can
  // compare against the primary. Adding/editing/removing an additional
  // option never touches price/depositAmount totals above, since those are
  // driven by the PRIMARY option only (unchanged, backward-compatible
  // behavior) - additional options are a client-facing "pick one" choice,
  // not additively priced into the quote total.
  const handleAddOption = (component) => {
    const currentOptions = getComponentOptions(component);
    const newOption = blankAdditionalOption(currentOptions.length);
    setQuote(prev => ({
      ...prev,
      components: prev.components.map(c =>
        c.id === component.id ? { ...c, options: [...currentOptions, newOption] } : c
      )
    }));
    return newOption.id;
  };

  const handleUpdateOption = (componentId, optionId, patch) => {
    setQuote(prev => ({
      ...prev,
      components: prev.components.map(c => {
        if (c.id !== componentId) return c;
        const options = getComponentOptions(c);
        return { ...c, options: options.map(o => o.id === optionId ? { ...o, ...patch } : o) };
      })
    }));
  };

  const handleDeleteOption = (componentId, optionId) => {
    setQuote(prev => ({
      ...prev,
      components: prev.components.map(c => {
        if (c.id !== componentId) return c;
        const options = getComponentOptions(c);
        return { ...c, options: options.filter(o => o.id !== optionId) };
      })
    }));
  };

  // Save a single quote option's payload to the API (create or update),
  // with a built-in retry if the backend rejects the request due to a
  // missing/legacy `notes` column. `isActive` controls whether the
  // component-level manual deposit override applies (that flag is tracked
  // once for whichever option is currently in view).
  const saveQuoteOptionPayload = async (id, quoteObj, sendToClient, isActive) => {
    const calculatedTotal = calculateTotalPrice(quoteObj.components);
    const calculatedDeposit = calculateTotalDeposit(quoteObj.components);
    const finalDeposit = (isActive && depositManuallyOverridden) ? quoteObj.depositAmount : calculatedDeposit;
    const isNew = !id;

    const quoteData = {
      title: quoteObj.quoteTitle,
      client_id: quoteObj.client?.id || null,
      client_name: `${quoteObj.client?.firstName || ''} ${quoteObj.client?.lastName || ''}`.trim(),
      client_email: quoteObj.client?.email || '',
      client_phone: quoteObj.client?.phone || '',
      travel_start_date: quoteObj.travelDates.start,
      travel_end_date: quoteObj.travelDates.end,
      adults: quoteObj.travelers.adults,
      children: quoteObj.travelers.children.length,
      children_ages: JSON.stringify(quoteObj.travelers.children.map(c => c.age)),
      components: JSON.stringify(quoteObj.components || []),
      options: JSON.stringify(quoteObj.options),
      total_amount: calculatedTotal,
      deposit_amount: finalDeposit || 0,
      deposit_due_date: quoteObj.depositDueDate || null,
      expires_at: quoteObj.expiryDate || null,
      notes: quoteObj.notes || '',
      template_id: selectedTemplate || 'modern'
    };

    // Only ever set `status` explicitly when we actually know what it
    // should be: sending always means 'sent', and a brand-new quote that
    // isn't being sent starts life as 'draft'. For an EXISTING quote that
    // isn't being sent right now (autosave, "Save Draft"/"Save Changes"),
    // deliberately omit `status` from the payload so the backend's
    // dynamic UPDATE (PUT /api/quotes/:id, allowedFields-based) leaves the
    // column untouched - otherwise every autosave silently reverted an
    // already-sent (or converted) quote back to 'draft', since autosave
    // always calls this with sendToClient=false regardless of the quote's
    // real current status (confirmed live: editing an already-sent
    // quote's trip details flipped it back to draft within ~2.5s, before
    // any Save/Send button was even clicked).
    if (sendToClient) {
      quoteData.status = 'sent';
    } else if (isNew) {
      quoteData.status = 'draft';
    }

    let response;
    try {
      response = isNew
        ? await quotesService.create(quoteData)
        : await quotesService.update(id, quoteData);
    } catch (err) {
      // If we get an error possibly related to a missing notes column, retry without it
      if (err.response?.status === 400 || err.message?.includes('notes') || err.message?.includes('column')) {
        console.warn(`Possible database schema issue with notes field, retrying without notes for quote ${id || '(new)'}`);
        const { notes, ...quoteDataWithoutNotes } = quoteData;
        response = isNew
          ? await quotesService.create(quoteDataWithoutNotes)
          : await quotesService.update(id, quoteDataWithoutNotes);
      } else {
        throw err;
      }
    }

    return { id: response.quoteId || response.id || id, response, isNew };
  };

  // Silent background save, debounced ~2.5s after the last edit (see the
  // useEffect below). Deliberately never sends to the client (sendToClient
  // is always false) and never navigates away - it only exists to persist
  // draft work-in-progress. Skipped entirely for the multi-option AI
  // comparison group, while an explicit save/send is already running, and
  // until Trip Details has actually been completed (detailsStepCompletedRef).
  const performAutosave = async () => {
    if (loading || quoteOptionGroup.length > 0 || !quote.client || !detailsStepCompletedRef.current) return;

    try {
      setSaveStatus('saving');
      const result = await saveQuoteOptionPayload(quoteId, quote, false, true);

      if (result.isNew && result.id) {
        setQuoteId(result.id);
        setQuote(prev => ({ ...prev, id: result.id }));
        window.history.replaceState(null, '', `${basePath}/quotes/${result.id}/edit`);
      }

      setSaveStatus('saved');
    } catch (error) {
      console.error('Autosave failed:', error);
      setSaveStatus('error');
    }
  };

  // Shared debounce scheduling, ~2.5s after the last edit. Used both by the
  // effect below (fires on every quote change) and by the Trip Details
  // "Continue" button, so that just-completed title/dates get persisted
  // even if the agent doesn't happen to touch anything else afterward.
  const scheduleAutosave = () => {
    setSaveStatus('unsaved');
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      performAutosave();
    }, 2500);
  };

  // Debounce: schedule a silent autosave ~2.5s after the agent stops
  // editing. skipNextAutosaveRef guards against firing right after a fresh
  // load/hydration (fetchQuote, AI quote generation) - those are not edits.
  useEffect(() => {
    if (skipNextAutosaveRef.current) {
      skipNextAutosaveRef.current = false;
      return;
    }
    if (!quote.client || quoteOptionGroup.length > 0 || !detailsStepCompletedRef.current) return;

    scheduleAutosave();

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [quote]);

  // Handle quote save. When the builder has a multi-option AI quote group
  // loaded, this snapshots the active tab's edits and then saves EVERY
  // option in the group in parallel, so agents never lose in-progress
  // edits on tabs they aren't currently viewing.
  const handleSaveQuote = async (sendToClient = false, stayOnPage = false, sendMode = 'current') => {
    setLoading(true);
    setErrors({});

    // An explicit save supersedes any pending debounced autosave.
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);

    const hasMultipleOptions = quoteOptionGroup.length > 0;

    // Snapshot the currently active tab's latest edits back into the group
    // before building the save list (mirrors handleSwitchOption's behavior).
    const syncedGroup = hasMultipleOptions
      ? quoteOptionGroup.map((option, idx) => (idx === activeOptionIndex ? { ...option, data: quote } : option))
      : [];
    if (hasMultipleOptions) {
      setQuoteOptionGroup(syncedGroup);
    }

    // Build the full list of quote records that need to be persisted:
    // every option in the comparison group, or just the single active quote.
    const optionsToSave = hasMultipleOptions
      ? syncedGroup.map((option, idx) => ({ id: option.id, quoteObj: option.data, optionName: option.optionName, isActive: idx === activeOptionIndex }))
      : [{ id: quoteId, quoteObj: quote, optionName: null, isActive: true }];

    // Validate deposits across every option being saved, not just the active one
    const allDepositErrors = [];
    optionsToSave.forEach(({ quoteObj, optionName }) => {
      const errs = validateDeposits(quoteObj.components, quoteObj.totalPrice);
      if (errs.length > 0) {
        const prefix = optionName ? `[${optionName}] ` : '';
        allDepositErrors.push(...errs.map(e => `${prefix}${e}`));
      }
    });
    if (allDepositErrors.length > 0) {
      setErrors({ deposit: allDepositErrors.join('. ') });
      setLoading(false);
      return;
    }

    console.log(`💾 Saving ${optionsToSave.length} quote option(s)...`);

    try {
      // Save every option in parallel, and wait for all of them to settle
      // (success or failure) before releasing the loading state.
      const settled = await Promise.allSettled(
        optionsToSave.map(({ id, quoteObj, isActive }) => saveQuoteOptionPayload(id, quoteObj, sendToClient, isActive))
      );

      const failedIndexes = settled
        .map((result, idx) => (result.status === 'rejected' ? idx : null))
        .filter(idx => idx !== null);

      if (failedIndexes.length > 0) {
        const failedLabels = failedIndexes
          .map(idx => optionsToSave[idx].optionName || (hasMultipleOptions ? `Option ${idx + 1}` : 'Quote'))
          .join(', ');
        const firstError = settled[failedIndexes[0]].reason;
        const detail = firstError?.response?.data?.error || firstError?.message || 'Unknown error';
        throw new Error(`Failed to save: ${failedLabels}. ${detail}`);
      }

      const results = settled.map(result => result.value);

      // Update ids/state for any newly-created quotes
      results.forEach((result, idx) => {
        if (result.isNew && (result.response.quoteId || result.response.id)) {
          const newQuoteId = result.response.quoteId || result.response.id;

          if (hasMultipleOptions) {
            setQuoteOptionGroup(prev => prev.map((option, i) => (i === idx ? { ...option, id: newQuoteId } : option)));
            if (idx === activeOptionIndex) {
              setQuoteId(newQuoteId);
              skipNextAutosaveRef.current = true;
              setQuote(prev => ({ ...prev, id: newQuoteId }));
            }
          } else {
            setQuoteId(newQuoteId);
            skipNextAutosaveRef.current = true;
            setQuote(prev => ({ ...prev, id: newQuoteId }));
            if (stayOnPage) {
              window.history.replaceState(null, '', `${basePath}/quotes/${newQuoteId}/edit`);
            }
          }
        }
      });

      // This explicit save just persisted everything the debounced
      // autosave would have - reflect that in the status indicator.
      setSaveStatus('saved');

      const activeResult = hasMultipleOptions ? results[activeOptionIndex] : results[0];
      const activeIdForSend = activeResult?.id || quoteId;

      if (sendToClient && activeIdForSend) {
        // Send email to client. sendMode='comparison' emails a link to the
        // tabbed comparison page covering every option; 'current' (default)
        // emails a link to just the option currently being viewed.
        const sendResponse = await quotesService.send(activeIdForSend, { sendMode });
        const wasComparisonSend = sendResponse?.isComparisonSend;
        toast.success(
          wasComparisonSend
            ? `Comparison of all ${sendResponse.optionCount} options sent successfully to ${quote.client?.email}`
            : 'Quote sent successfully to ' + quote.client?.email +
              (hasMultipleOptions ? ` (all ${results.length} options saved)` : '')
        );
      } else if (!sendToClient) {
        toast.success(
          hasMultipleOptions
            ? `All ${results.length} quote options saved successfully!`
            : (isEditMode ? 'Quote updated successfully!' : 'Quote saved as draft!')
        );
      }

      // Only navigate away if not staying on page
      if (!stayOnPage) {
        navigate(`${basePath}/quotes`);
      }

      return activeResult?.response; // Return the active option's response for further use
    } catch (error) {
      console.error('Error saving quote:', error);
      let errorMessage = 'Failed to save quote. Please try again.';

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setErrors({ submit: errorMessage });
      toast.error(errorMessage);
      setSaveStatus('error');
    } finally {
      setLoading(false);
    }
  };

  // Known "primary date" field names across the component forms (camelCase
  // for general forms, snake_case for the brand package forms). Used to
  // auto-clear the "dates not confirmed yet" reminder below once the agent
  // has actually filled in a real date for the component.
  const PRIMARY_DATE_FIELD_KEYS = [
    'checkInDate', 'check_in_date', 'departureDate', 'departure_date',
    'pickupDate', 'activityDate', 'date', 'reservationDate'
  ];

  // Voyager AI concierge leads only ever capture a freeform `timeframe`
  // (e.g. "June 2027"), never exact dates - see
  // backend/utils/conciergeLeadToQuoteComponent.js. This surfaces that as a
  // reminder on the component card so the agent doesn't forget to pick
  // real dates before sending, without ever exposing it to the client
  // (requestedTimeframe/needsDatesConfirmed are agent-only fields that
  // ModernQuoteView.js does not read).
  const needsDateReminder = (component) => {
    if (!component?.needsDatesConfirmed || !component?.requestedTimeframe) return false;
    const data = component.data || {};
    return !PRIMARY_DATE_FIELD_KEYS.some((key) => !!data[key]);
  };

  // Render component form based on type
  const renderComponentForm = () => {
    // For a brand-new (non-editing) component, prefill any brand/label
    // default the chosen catalog shortcut has (e.g. clicking "Norwegian
    // Cruise Line" prefills CruiseForm's Cruise Line field) - see
    // componentTypeDefaults in componentFormRegistry.js.
    const initialData = getComponentFormInitialData(editingComponent) || componentTypeDefaults[selectedComponentType] || null;

    const props = {
      onSave: handleAddComponent,
      onCancel: () => {
        setShowComponentModal(false);
        setSelectedComponentType('');
        setEditingComponent(null);
      },
      initialData: initialData
    };

    // If selectedComponentType is missing but we have editingComponent, try to infer it
    let effectiveComponentType = selectedComponentType;
    if (!effectiveComponentType && editingComponent) {
      effectiveComponentType = editingComponent.type || inferComponentType(editingComponent);
    }

    return renderComponentTypeForm(effectiveComponentType, props);
  };

  // Whether the agent can move forward from a given step - mirrors the
  // Figma "Voyager Pro" design's canAdvance(), adapted to our real state.
  // Step 3 keeps our existing (stricter than Figma's) requirement of at
  // least one component before Review & Send, since that was already
  // enforced and relaxing it would let an agent send an empty quote.
  const isStepReady = (stepId) => {
    if (stepId === 'client') return !!quote.client;
    if (stepId === 'details') return !!(quote.quoteTitle && quote.travelDates.start && quote.travelDates.end);
    if (stepId === 'components') return (Array.isArray(quote.components) ? quote.components : []).length > 0;
    return true;
  };

  // Push live step/progress/quote-summary state up to the shared sidebar
  // shell (TravelieLayout) via context so it can render the Figma-style
  // step nav + quote summary while this page is mounted, and clear it again
  // on unmount so other pages see the normal nav.
  useEffect(() => {
    setNavState({
      steps: QUOTE_BUILDER_STEPS,
      activeStep,
      isStepReady,
      goToStep: (stepId) => setActiveStep(stepId),
      client: quote.client,
      tripTitle: quote.quoteTitle,
      travelDates: quote.travelDates,
      componentsCount: (Array.isArray(quote.components) ? quote.components : []).length,
    });
    return () => setNavState(null);
    // eslint-disable-next-line
  }, [activeStep, quote.client, quote.quoteTitle, quote.travelDates, quote.components]);

  if (loadingQuote) {
    return (
      <div className="loading-container">
        <div className="loading">Loading quote...</div>
      </div>
    );
  }

  const activeStepIndex = QUOTE_BUILDER_STEPS.findIndex(s => s.id === activeStep);
  const progressPercent = Math.round(((activeStepIndex + 1) / QUOTE_BUILDER_STEPS.length) * 100);

  return (
    <div className="quote-builder">
      {/* Step X of 4 + progress bar now live in TravelieLayout's shared top
          bar (see QuoteBuilderNavContext) so there's a single header instead
          of a duplicate one here. Autosave status still shown locally since
          it's specific to this page's save lifecycle. */}
      {saveStatus !== 'idle' && quoteOptionGroup.length === 0 && (
        <div className="flex items-center justify-end px-4 sm:px-6 pt-3">
          <span className={`autosave-status autosave-status-${saveStatus}`}>
            {saveStatus === 'saving' && 'Saving…'}
            {saveStatus === 'saved' && '✓ All changes saved'}
            {saveStatus === 'unsaved' && 'Unsaved changes'}
            {saveStatus === 'error' && '⚠ Save failed'}
          </span>
        </div>
      )}

      {/* Multi-Option AI Quote Comparison Tabs */}
      <QuoteOptionTabs
        options={quoteOptionGroup.map((option, idx) => ({
          id: option.id,
          optionName: option.optionName,
          // Show live totals for the active tab (in case the agent is mid-edit),
          // and the last-synced totals for inactive tabs.
          totalPrice: idx === activeOptionIndex ? quote.totalPrice : option.data?.totalPrice
        }))}
        activeIndex={activeOptionIndex}
        onSelect={handleSwitchOption}
      />

      {/* Step Content */}
      <div className="quote-builder-content">
        {/* Client Selection Step */}
        {activeStep === 'client' && (
          <div className="max-w-3xl mx-auto px-4">
            <div className="mb-8">
              <h2 className="text-3xl mb-1" style={{ fontFamily: 'var(--font-display)', color: 'var(--foreground)' }}>Select Client</h2>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Search for an existing client or add a new one to begin building their quote.</p>
            </div>

            <div className="space-y-4">
              {/* Existing/New Client toggle - "New Client" opens the full
                  client-creation modal (passport/travel-doc/emergency
                  contact fields), which is richer than a simple inline
                  form so it's kept as a modal rather than reimplemented. */}
              <div className="flex gap-1 p-1 rounded-xl w-fit" style={{ background: 'var(--muted)' }}>
                <button
                  type="button"
                  className="px-5 py-2 rounded-lg text-sm font-medium transition-all"
                  style={{ background: 'white', color: 'var(--foreground)', boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}
                >
                  Existing Client
                </button>
                <button
                  type="button"
                  onClick={() => setShowClientModal(true)}
                  className="px-5 py-2 rounded-lg text-sm font-medium transition-all"
                  style={{ color: 'var(--muted-foreground)' }}
                >
                  New Client
                </button>
              </div>

              <div className="relative">
                <UserSearch className="absolute left-4 top-1/2 -translate-y-1/2" size={16} style={{ color: 'var(--muted-foreground)' }} />
                <input
                  type="text"
                  placeholder="Search by name or email…"
                  value={clientSearchQuery}
                  onChange={(e) => setClientSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 border rounded-xl text-sm transition-colors focus:outline-none"
                  style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}
                />
              </div>

              <div className="grid gap-3">
                {clients
                  .filter(c => {
                    const q = clientSearchQuery.toLowerCase();
                    const name = `${c.firstName || c.first_name || ''} ${c.lastName || c.last_name || ''}`.toLowerCase();
                    return !q || name.includes(q) || (c.email || '').toLowerCase().includes(q);
                  })
                  .slice(0, 25)
                  .map(client => {
                    const isSelected = quote.client && String(quote.client.id) === String(client.id);
                    return (
                      <button
                        key={client.id}
                        type="button"
                        onClick={() => handleClientSelect(client)}
                        className="w-full text-left p-4 rounded-xl border-2 transition-all flex items-center justify-between hover:shadow-sm"
                        style={isSelected
                          ? { borderColor: 'var(--accent)', background: 'rgba(196, 135, 31, 0.08)' }
                          : { borderColor: 'var(--border)' }}
                      >
                        <div className="flex items-center gap-3.5">
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0"
                            style={isSelected ? { background: 'var(--accent)', color: 'white' } : { background: 'var(--muted)', color: 'var(--foreground)' }}
                          >
                            {(client.firstName || client.first_name || '?')[0]}{(client.lastName || client.last_name || '')[0]}
                          </div>
                          <div>
                            <div className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>{client.firstName || client.first_name} {client.lastName || client.last_name}</div>
                            <div className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{client.email}{client.phone ? ` · ${client.phone}` : ''}</div>
                          </div>
                        </div>
                        {isSelected ? (
                          <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--accent)' }}>
                            <Check size={14} style={{ color: 'white' }} />
                          </div>
                        ) : (
                          <ChevronRight size={16} style={{ color: 'var(--muted-foreground)' }} />
                        )}
                      </button>
                    );
                  })}

                {clients.length === 0 && (
                  <div className="text-center py-10 text-sm" style={{ color: 'var(--muted-foreground)' }}>No clients yet — add your first one below.</div>
                )}
                {clients.length > 0 && clients.filter(c => {
                  const q = clientSearchQuery.toLowerCase();
                  const name = `${c.firstName || c.first_name || ''} ${c.lastName || c.last_name || ''}`.toLowerCase();
                  return !q || name.includes(q) || (c.email || '').toLowerCase().includes(q);
                }).length === 0 && (
                  <div className="text-center py-10 text-sm" style={{ color: 'var(--muted-foreground)' }}>
                    No clients match your search.{' '}
                    <button type="button" className="font-medium hover:underline" style={{ color: 'var(--accent)' }} onClick={() => setShowClientModal(true)}>Add a new client?</button>
                  </div>
                )}
              </div>

              {/* Persistent "selected" confirmation bar, matching Figma -
                  stays visible below the list once a client is picked. */}
              {quote.client && (
                <div className="flex items-center justify-between p-4 rounded-xl border-2" style={{ borderColor: 'var(--accent)', background: 'rgba(196, 135, 31, 0.08)' }}>
                  <div className="flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0" style={{ background: 'var(--accent)', color: 'white' }}>
                      <Check size={16} />
                    </div>
                    <div>
                      <div className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>{quote.client.firstName} {quote.client.lastName} selected</div>
                      <div className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{quote.client.email}</div>
                    </div>
                  </div>
                  <button type="button" onClick={() => setQuote(prev => ({ ...prev, client: null }))} className="text-sm font-medium hover:underline flex-shrink-0" style={{ color: 'var(--accent)' }}>
                    Change
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Trip Details Step */}
        {activeStep === 'details' && (
          <div className="max-w-3xl mx-auto px-4">
            <div className="mb-8">
              <h2 className="text-3xl mb-1" style={{ fontFamily: 'var(--font-display)', color: 'var(--foreground)' }}>Trip Details</h2>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Set the core parameters for this travel quote.</p>
            </div>
            <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
              {/* Quote Title */}
              <div className="bg-white border rounded-xl p-6" style={{ borderColor: 'var(--border)' }}>
                <label className="block text-xs font-semibold mb-2 uppercase tracking-widest" style={{ color: 'var(--muted-foreground)' }}>
                  Quote Title*
                </label>
                <input
                  type="text"
                  value={quote.quoteTitle}
                  onChange={(e) => setQuote(prev => ({ ...prev, quoteTitle: e.target.value }))}
                  placeholder="e.g., Smith Family Caribbean Cruise"
                  className="w-full px-0 py-1 border-0 border-b-2 focus:outline-none bg-transparent text-xl font-medium"
                  style={{ borderColor: 'var(--border)', color: 'var(--foreground)', fontFamily: 'var(--font-display)' }}
                  required
                />
              </div>

              {/* Travel Dates */}
              <div className="bg-white border rounded-xl p-6" style={{ borderColor: 'var(--border)' }}>
                <div className="flex items-center gap-2 mb-4">
                  <Calendar size={16} style={{ color: 'var(--accent)' }} />
                  <h3 className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--muted-foreground)' }}>Travel Dates</h3>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                      Start Date*
                    </label>
                    <input
                      type="date"
                      value={quote.travelDates.start}
                      onChange={(e) => setQuote(prev => ({ 
                        ...prev, 
                        travelDates: { ...prev.travelDates, start: e.target.value }
                      }))}
                      className="w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none transition-colors"
                      style={{ borderColor: 'var(--border)', color: 'var(--foreground)', background: 'var(--muted)' }}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--muted-foreground)' }}>
                      End Date*
                    </label>
                    <input
                      type="date"
                      value={quote.travelDates.end}
                      onChange={(e) => setQuote(prev => ({ 
                        ...prev, 
                        travelDates: { ...prev.travelDates, end: e.target.value }
                      }))}
                      min={quote.travelDates.start}
                      className="w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none transition-colors"
                      style={{ borderColor: 'var(--border)', color: 'var(--foreground)', background: 'var(--muted)' }}
                      required
                    />
                  </div>
                </div>
                {quote.travelDates.start && quote.travelDates.end && (() => {
                  const nights = Math.max(0, Math.round((new Date(quote.travelDates.end) - new Date(quote.travelDates.start)) / 86400000));
                  return (
                    <div className="mt-4">
                      <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold" style={{ background: 'rgba(196, 135, 31, 0.1)', color: 'var(--accent)' }}>
                        {nights} night{nights !== 1 ? 's' : ''} · {nights + 1} days
                      </span>
                    </div>
                  );
                })()}
              </div>

              {/* Travelers Section */}
              <div className="bg-white border rounded-xl p-6" style={{ borderColor: 'var(--border)' }}>
                <div className="flex items-center gap-2 mb-5">
                  <Users size={16} style={{ color: 'var(--muted-foreground)' }} />
                  <h3 className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--muted-foreground)' }}>Travelers</h3>
                </div>

                <div className="flex items-center justify-between mb-5">
                  <div>
                    <div className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Adults</div>
                    <div className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Age 18+</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setQuote(prev => ({ ...prev, travelers: { ...prev.travelers, adults: Math.max(1, prev.travelers.adults - 1) } }))}
                      className="w-9 h-9 rounded-full border-2 flex items-center justify-center text-lg font-light transition-colors"
                      style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}
                    >
                      −
                    </button>
                    <span className="w-8 text-center text-lg font-semibold" style={{ color: 'var(--foreground)' }}>{quote.travelers.adults}</span>
                    <button
                      type="button"
                      onClick={() => setQuote(prev => ({ ...prev, travelers: { ...prev.travelers, adults: prev.travelers.adults + 1 } }))}
                      className="w-9 h-9 rounded-full border-2 flex items-center justify-center text-lg font-light transition-colors"
                      style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="border-t mb-5" style={{ borderColor: 'var(--border)' }} />

                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Children</div>
                    <div className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Age 0–17</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        const count = Math.max(0, quote.travelers.children.length - 1);
                        setQuote(prev => ({ ...prev, travelers: { ...prev.travelers, children: prev.travelers.children.slice(0, count) } }));
                      }}
                      className="w-9 h-9 rounded-full border-2 flex items-center justify-center text-lg font-light transition-colors"
                      style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}
                    >
                      −
                    </button>
                    <span className="w-8 text-center text-lg font-semibold" style={{ color: 'var(--foreground)' }}>{quote.travelers.children.length}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setQuote(prev => ({
                          ...prev,
                          travelers: {
                            ...prev.travelers,
                            children: [...prev.travelers.children, { age: 5 }]
                          }
                        }));
                      }}
                      className="w-9 h-9 rounded-full border-2 flex items-center justify-center text-lg font-light transition-colors"
                      style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Children List */}
                {quote.travelers.children.length > 0 && (
                  <div className="space-y-3 pt-5">
                    <div className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>Children's Ages</div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {quote.travelers.children.map((child, index) => (
                        <div key={index}>
                          <label className="block text-xs mb-1 text-center" style={{ color: 'var(--muted-foreground)' }}>Child {index + 1}</label>
                          <select
                            value={child.age}
                            onChange={(e) => {
                              const newChildren = [...quote.travelers.children];
                              newChildren[index] = { age: parseInt(e.target.value) };
                              setQuote(prev => ({
                                ...prev,
                                travelers: { ...prev.travelers, children: newChildren }
                              }));
                            }}
                            className="w-full px-3 py-2 border rounded-lg text-sm text-center appearance-none cursor-pointer transition-colors"
                            style={{ borderColor: 'var(--border)', color: 'var(--foreground)', background: 'color-mix(in srgb, var(--muted) 30%, transparent)' }}
                            onFocus={(e) => { e.target.style.borderColor = 'var(--accent)'; e.target.style.outline = 'none'; }}
                            onBlur={(e) => { e.target.style.borderColor = 'var(--border)'; }}
                          >
                            {Array.from({ length: 18 }, (_, age) => (
                              <option key={age} value={age}>{age === 0 ? 'Under 1' : `${age} yr${age !== 1 ? 's' : ''}`}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quote expiry */}
                <div className="border-t pt-4" style={{ borderColor: 'var(--border)' }}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Quote Valid Until</div>
                      <div className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Client must accept by this date</div>
                    </div>
                    <div className="flex-shrink-0" style={{ width: 176 }}>
                      <input
                        type="date"
                        value={quote.expiryDate || ''}
                        min={new Date().toISOString().split('T')[0]}
                        onChange={(e) => setQuote(prev => ({ ...prev, expiryDate: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none transition-colors"
                        style={{ borderColor: 'var(--border)', color: 'var(--foreground)', background: 'var(--muted)' }}
                      />
                    </div>
                  </div>
                  {quote.expiryDate && (
                    <div className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg border" style={{ color: '#92400e', background: '#fffbeb', borderColor: '#fde68a' }}>
                      <Calendar size={12} />
                      This quote expires on {new Date(quote.expiryDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </div>
                  )}
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Components Step */}
        {activeStep === 'components' && (
          <div className="max-w-5xl mx-auto px-4">
            <div className="mb-6">
              <h2 className="text-3xl mb-1" style={{ fontFamily: 'var(--font-display)', color: 'var(--foreground)' }}>Components</h2>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Add hotels, flights, cruises, and more to build out this trip.</p>
            </div>

            {/* Temporarily hidden - incomplete features
            {user?.email === 'chapmaninvestmentgroupllc@gmail.com' && (
              <>
                <button
                  className="ai-quote-builder-btn"
                  onClick={() => setShowConversationalBuilder(true)}
                >
                  🤖 AI Quote Builder
                </button>
                <button
                  className="ai-quote-builder-btn secondary"
                  onClick={() => setShowPDFUploadModal(true)}
                >
                  📄 Quick PDF Upload
                </button>
              </>
            )}
            */}
            <div className="flex items-center gap-2 mb-4">
              <button
                type="button"
                onClick={() => setShowLibraryModal(true)}
                className="flex items-center gap-1.5 px-3.5 py-2 border rounded-lg text-xs font-semibold transition-colors"
                style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}
              >
                📚 Component Library
              </button>
            </div>

            {/* Category tabs - filled navy for active + a live count badge,
                matching the Figma "Voyager Pro" design */}
            <div className="flex gap-2 overflow-x-auto pb-1 mb-6">
              {componentCategories.map(category => {
                const categoryComponentTypeIds = new Set(category.components.map(t => t.id));
                const count = (Array.isArray(quote.components) ? quote.components : [])
                  .filter(c => c && c.title && categoryComponentTypeIds.has(c.type || inferComponentType(c))).length;
                const isActiveCategory = activeComponentCategory === category.id;
                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => setActiveComponentCategory(category.id)}
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all flex-shrink-0 border"
                    style={isActiveCategory
                      ? { background: 'var(--primary)', color: 'white', borderColor: 'var(--primary)' }
                      : { background: 'white', color: 'var(--muted-foreground)', borderColor: 'var(--border)' }}
                  >
                    <span>{category.icon}</span>
                    <span>{category.name}</span>
                    {count > 0 && (
                      <span
                        className="ml-1 px-1.5 py-0.5 rounded-full text-xs font-semibold"
                        style={isActiveCategory ? { background: 'rgba(255,255,255,0.2)', color: 'white' } : { background: 'rgba(196, 135, 31, 0.1)', color: 'var(--accent)' }}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
              {/* Catalog - left column */}
              <div className="lg:col-span-2 space-y-4">
                <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--muted-foreground)' }}>Add Component</div>
                <div className="space-y-2">
                  {(componentCategories.find(c => c.id === activeComponentCategory)?.components || []).map(type => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => {
                        setEditingComponent(null);
                        setSelectedComponentType(type.id);
                        setShowComponentModal(true);
                      }}
                      className="w-full flex items-center gap-3 p-3.5 bg-white border rounded-xl text-left transition-all hover:shadow-sm"
                      style={{ borderColor: 'var(--border)' }}
                    >
                      <span className="text-xl flex-shrink-0">{type.icon}</span>
                      <span className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>{type.name}</span>
                      <Plus size={16} className="ml-auto flex-shrink-0" style={{ color: 'var(--muted-foreground)' }} />
                    </button>
                  ))}
                </div>

                {/* Travel party summary */}
                <div className="p-4 rounded-xl border" style={{ background: 'color-mix(in srgb, var(--muted) 50%, transparent)', borderColor: 'var(--border)' }}>
                  <div className="text-xs font-semibold mb-2" style={{ color: 'var(--foreground)' }}>Travel Party</div>
                  <div className="flex gap-3 flex-wrap">
                    <div className="flex items-center gap-1.5">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--primary)' }}>
                        <Users size={10} style={{ color: 'white' }} />
                      </div>
                      <span className="text-xs font-semibold" style={{ color: 'var(--foreground)' }}>{quote.travelers.adults} adult{quote.travelers.adults !== 1 ? 's' : ''}</span>
                    </div>
                    {quote.travelers.children.length > 0 && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm">👧</span>
                        <span className="text-xs font-semibold" style={{ color: 'var(--foreground)' }}>
                          {quote.travelers.children.length} child{quote.travelers.children.length !== 1 ? 'ren' : ''}
                        </span>
                        <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                          ({quote.travelers.children.map(c => `${c.age}yo`).join(', ')})
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Multiple options tip */}
                <div className="p-4 rounded-xl border" style={{ background: '#fffbeb', borderColor: '#fde68a' }}>
                  <div className="flex items-start gap-2.5">
                    <AlertCircle size={14} className="flex-shrink-0 mt-0.5" style={{ color: 'var(--accent)' }} />
                    <div>
                      <div className="text-xs font-semibold mb-1" style={{ color: 'var(--foreground)' }}>Multiple Options</div>
                      <p className="text-xs leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                        Expand any component and click <strong>Add Option</strong> to give your client alternatives to compare.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Added components - right column, scoped to active category */}
              <div className="lg:col-span-3 space-y-3">
                {/* Inline "new component" form, shown right after picking a
                    catalog item - expands in place instead of opening a
                    modal (matches Figma's accordion pattern). */}
                {showComponentModal && !editingComponent?.id && selectedComponentType && (
                  <div className="bg-white border-2 rounded-xl overflow-hidden" style={{ borderColor: 'var(--accent)' }}>
                    <div className="flex items-center justify-between gap-3 px-4 py-3" style={{ background: 'rgba(196, 135, 31, 0.06)' }}>
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{componentTypes.find(t => t.id === selectedComponentType)?.icon}</span>
                        <h4 className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>
                          New {componentTypes.find(t => t.id === selectedComponentType)?.name}
                        </h4>
                      </div>
                      <button
                        type="button"
                        className="text-xs font-medium px-2.5 py-1 rounded-lg transition-colors flex-shrink-0"
                        style={{ background: 'var(--muted)', color: 'var(--foreground)' }}
                        onClick={() => { setShowComponentModal(false); setSelectedComponentType(''); }}
                      >
                        Cancel
                      </button>
                    </div>
                    <div className="p-4 border-t" style={{ borderColor: 'var(--border)' }}>
                      {renderComponentForm()}
                    </div>
                  </div>
                )}

                {(() => {
                  const categoryComponentTypeIds = new Set((componentCategories.find(c => c.id === activeComponentCategory)?.components || []).map(t => t.id));
                  const categoryComponents = (Array.isArray(quote.components) ? quote.components : [])
                    .filter(component => component && component.title && component.title !== 'undefined' && component.title.trim() !== '')
                    .filter(component => categoryComponentTypeIds.has(component.type || inferComponentType(component)));

                  if (categoryComponents.length === 0) {
                    if (showComponentModal && !editingComponent?.id && selectedComponentType) return null;
                    return (
                      <div className="flex flex-col items-center justify-center py-16 text-center bg-white border-2 border-dashed rounded-xl" style={{ borderColor: 'var(--border)' }}>
                        <span className="text-4xl mb-3">{componentCategories.find(c => c.id === activeComponentCategory)?.icon}</span>
                        <div className="text-sm font-medium mb-1" style={{ color: 'var(--foreground)' }}>No components yet</div>
                        <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>Select from the left to add components</div>
                      </div>
                    );
                  }

                  return (
                    <>
                      <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--muted-foreground)' }}>Added ({categoryComponents.length})</div>
                      {categoryComponents.map(component => {
                      // Check for isOption in both locations for compatibility
                      const isOption = component.isOption || component.data?.isOption || false;
                      const optionDescription = component.optionDescription || component.data?.optionDescription || '';
                      const isExpanded = showComponentModal && editingComponent?.id === component.id;
                      return (
                        <div key={component.id} className="bg-white border rounded-xl overflow-hidden" style={{ borderColor: isExpanded ? 'var(--accent)' : isOption ? 'var(--accent)' : 'var(--border)' }}>
                          <div className="flex items-start justify-between gap-2 p-4">
                            <button
                              type="button"
                              className="flex-1 flex items-start gap-2.5 text-left min-w-0"
                              onClick={() => {
                                if (isExpanded) {
                                  setShowComponentModal(false);
                                  setEditingComponent(null);
                                  setSelectedComponentType('');
                                } else {
                                  const componentType = component.type || inferComponentType(component);
                                  setEditingComponent(component);
                                  setSelectedComponentType(componentType);
                                  setShowComponentModal(true);
                                }
                              }}
                            >
                              <span className="text-lg flex-shrink-0">{componentTypes.find(t => t.id === component.type)?.icon}</span>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h4 className="font-semibold text-sm" style={{ color: 'var(--foreground)' }}>{component.title}</h4>
                                  {isOption && (
                                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: 'rgba(196, 135, 31, 0.1)', color: 'var(--accent)' }}>Optional</span>
                                  )}
                                  {hasMultipleOptions(component) && (
                                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: 'rgba(37,99,235,0.1)', color: '#2563EB' }}>{getComponentOptions(component).length} options</span>
                                  )}
                                </div>
                                {!isExpanded && (
                                  <>
                                    {needsDateReminder(component) && (
                                      <p className="text-xs mt-1.5 px-2.5 py-1.5 rounded-lg" style={{ background: '#fffbeb', color: '#92400e' }}>
                                        🗓️ Client requested <strong>{component.requestedTimeframe}</strong> - exact dates not selected yet
                                      </p>
                                    )}
                                    {component.description && <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>{component.description}</p>}
                                    {isOption && optionDescription && (
                                      <p className="text-xs mt-1 italic" style={{ color: 'var(--muted-foreground)' }}><strong>Client sees:</strong> {optionDescription}</p>
                                    )}
                                    {isOption && component.paymentTiming && (
                                      <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>
                                        <strong>Payment:</strong> {
                                          component.paymentTiming === 'immediate' ? '💳 Due immediately when selected' :
                                          component.paymentTiming === 'with_deposit' ? `💰 $${parseFloat(component.optionalDepositAmount || 0).toFixed(2)} deposit when selected` :
                                          component.paymentTiming === 'bundled' ? '📦 Bundled with package' :
                                          '📅 Due with final payment'
                                        }
                                      </p>
                                    )}
                                    <p className="text-sm font-bold mt-1" style={{ color: 'var(--foreground)' }}>
                                      {component.includedInPackage ? (
                                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>Included in Package Price</span>
                                      ) : (
                                        `$${(parseFloat(component.price) || 0).toFixed(2)}`
                                      )}
                                    </p>
                                  </>
                                )}
                              </div>
                            </button>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              {isExpanded ? (
                                <ChevronUp size={16} style={{ color: 'var(--muted-foreground)' }} />
                              ) : (
                                <ChevronDown size={16} style={{ color: 'var(--muted-foreground)' }} />
                              )}
                              <button
                                type="button"
                                className="text-xs font-medium px-2.5 py-1 rounded-lg text-red-600 transition-colors hover:bg-red-50"
                                onClick={() => handleDeleteComponent(component.id)}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="p-4 border-t" style={{ borderColor: 'var(--border)' }}>
                              {renderComponentForm()}

                              {/* Additional options for client comparison (Quote Builder
                                  Phase 2) - purely additive, doesn't affect the component's
                                  own price/deposit totals above. */}
                              <div className="mt-5 pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
                                <div className="flex items-center justify-between mb-3">
                                  <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--muted-foreground)' }}>
                                    Additional Options for Client to Compare
                                  </span>
                                  <button
                                    type="button"
                                    className="text-xs font-medium px-2.5 py-1 rounded-lg transition-colors"
                                    style={{ background: 'var(--muted)', color: 'var(--foreground)' }}
                                    onClick={() => setExpandedOptionId(handleAddOption(component))}
                                  >
                                    + Add Option
                                  </button>
                                </div>

                                {getComponentOptions(component).slice(1).length === 0 ? (
                                  <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                                    No additional options yet. Add one so the client can pick between alternatives (e.g. a standard vs. upgraded room).
                                  </p>
                                ) : (
                                  <div className="space-y-2">
                                    {getComponentOptions(component).slice(1).map(option => {
                                      const optionOpen = expandedOptionId === option.id;
                                      return (
                                        <div key={option.id} className="border rounded-lg overflow-hidden" style={{ borderColor: 'var(--border)' }}>
                                          <div className="flex items-center justify-between gap-2 p-2.5" style={{ background: 'var(--muted)' }}>
                                            <button
                                              type="button"
                                              className="flex-1 flex items-center gap-2 text-left"
                                              onClick={() => setExpandedOptionId(optionOpen ? null : option.id)}
                                            >
                                              <span className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>{option.label || 'Option'}</span>
                                              {option.badge && (
                                                <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: 'rgba(196,135,31,0.1)', color: 'var(--accent)' }}>{option.badge}</span>
                                              )}
                                              <span className="text-xs ml-auto" style={{ color: 'var(--muted-foreground)' }}>${getOptionTotal(option).toFixed(2)}</span>
                                            </button>
                                            {optionOpen ? <ChevronUp size={14} style={{ color: 'var(--muted-foreground)' }} /> : <ChevronDown size={14} style={{ color: 'var(--muted-foreground)' }} />}
                                            <button
                                              type="button"
                                              className="text-xs font-medium text-red-600 hover:bg-red-50 px-2 py-1 rounded-lg transition-colors"
                                              onClick={() => { handleDeleteOption(component.id, option.id); if (optionOpen) setExpandedOptionId(null); }}
                                            >
                                              Delete
                                            </button>
                                          </div>
                                          {optionOpen && (
                                            <div className="p-3 space-y-3 bg-white">
                                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                <div>
                                                  <label className="block text-xs font-medium mb-1 uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>Option Label</label>
                                                  <input type="text" value={option.label || ''}
                                                    onChange={(e) => handleUpdateOption(component.id, option.id, { label: e.target.value })}
                                                    placeholder="e.g., Ocean View Upgrade"
                                                    className="w-full px-3 py-2 border rounded-lg text-sm" style={{ borderColor: 'var(--border)' }} />
                                                </div>
                                                <div>
                                                  <label className="block text-xs font-medium mb-1 uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>Price ($)</label>
                                                  <input type="number" min="0" step="0.01" value={option.price ?? ''}
                                                    onChange={(e) => handleUpdateOption(component.id, option.id, { price: parseFloat(e.target.value) || 0 })}
                                                    placeholder="0.00"
                                                    className="w-full px-3 py-2 border rounded-lg text-sm" style={{ borderColor: 'var(--border)' }} />
                                                </div>
                                              </div>
                                              <div>
                                                <label className="block text-xs font-medium mb-1 uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>Badge (optional)</label>
                                                <input type="text" value={option.badge || ''}
                                                  onChange={(e) => handleUpdateOption(component.id, option.id, { badge: e.target.value })}
                                                  placeholder="e.g., Most Popular, Best Value"
                                                  className="w-full px-3 py-2 border rounded-lg text-sm" style={{ borderColor: 'var(--border)' }} />
                                              </div>
                                              <div>
                                                <label className="block text-xs font-medium mb-1 uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>Client-Visible Description</label>
                                                <textarea rows="2" value={option.description || ''}
                                                  onChange={(e) => handleUpdateOption(component.id, option.id, { description: e.target.value })}
                                                  placeholder="What makes this option different?"
                                                  className="w-full px-3 py-2 border rounded-lg text-sm" style={{ borderColor: 'var(--border)' }} />
                                              </div>
                                              <div>
                                                <label className="block text-xs font-medium mb-1 uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>Photo URL (optional)</label>
                                                <input type="url" value={option.photoUrl || ''}
                                                  onChange={(e) => handleUpdateOption(component.id, option.id, { photoUrl: e.target.value || null })}
                                                  placeholder="https://..."
                                                  className="w-full px-3 py-2 border rounded-lg text-sm" style={{ borderColor: 'var(--border)' }} />
                                              </div>
                                              <div className="pt-2 border-t" style={{ borderColor: 'var(--border)' }}>
                                                <p className="text-xs font-semibold mb-2" style={{ color: 'var(--muted-foreground)' }}>Internal only - never shown to client</p>
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                  <div>
                                                    <label className="block text-xs font-medium mb-1 uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>Agent Notes</label>
                                                    <input type="text" value={option.agentNotes || ''}
                                                      onChange={(e) => handleUpdateOption(component.id, option.id, { agentNotes: e.target.value })}
                                                      className="w-full px-3 py-2 border rounded-lg text-sm" style={{ borderColor: 'var(--border)' }} />
                                                  </div>
                                                  <div>
                                                    <label className="block text-xs font-medium mb-1 uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>Your Commission ($)</label>
                                                    <input type="number" min="0" step="0.01" value={option.agentCommission ?? ''}
                                                      onChange={(e) => handleUpdateOption(component.id, option.id, { agentCommission: e.target.value === '' ? null : parseFloat(e.target.value) })}
                                                      placeholder="0.00"
                                                      className="w-full px-3 py-2 border rounded-lg text-sm" style={{ borderColor: 'var(--border)' }} />
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Preview Step */}
        {activeStep === 'preview' && (
          <div className="step-content">
            <div className="max-w-4xl mx-auto" style={{ marginBottom: '24px' }}>
              <h2 className="text-3xl mb-1" style={{ fontFamily: 'var(--font-display)', color: 'var(--foreground)' }}>Review & Send</h2>
              <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Review every component and the client experience before sending.</p>
            </div>

            {/* Quote status snapshot */}
            <div className="max-w-4xl mx-auto bg-white border rounded-xl p-5 mb-6" style={{ borderColor: 'var(--border)' }}>
              <div className="flex items-center gap-0">
                {['draft', 'sent', 'viewed', 'accepted'].map((s, i, arr) => {
                  const order = { draft: 0, sent: 1, viewed: 2, accepted: 3, rejected: 3 };
                  const currentOrder = order[quote.status] ?? 0;
                  const stepOrder = order[s];
                  const isDone = currentOrder > stepOrder;
                  const isActive = currentOrder === stepOrder;
                  const labels = { draft: 'Draft', sent: 'Sent', viewed: 'Viewed', accepted: 'Accepted' };
                  // Most recent status_history entry for this stage, if any -
                  // shows the date it happened under the label (Figma pattern).
                  const event = (Array.isArray(quote.statusHistory) ? quote.statusHistory : [])
                    .filter(e => e.status === s)
                    .slice(-1)[0];
                  return (
                    <div key={s} className="flex items-center flex-1 min-w-0">
                      <div className="flex flex-col items-center flex-shrink-0">
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all"
                          style={isDone
                            ? { background: 'var(--accent)', color: 'white' }
                            : isActive
                              ? { background: 'var(--primary)', color: 'white', boxShadow: '0 0 0 4px rgba(30,58,95,0.15)' }
                              : { background: 'var(--muted)', color: 'var(--muted-foreground)' }}
                        >
                          {isDone ? <Check size={12} /> : i + 1}
                        </div>
                        <div className="text-xs mt-1 font-medium text-center w-16 truncate" style={{ color: isActive ? 'var(--primary)' : isDone ? 'var(--accent)' : 'var(--muted-foreground)' }}>
                          {labels[s]}
                        </div>
                        {event && (
                          <div className="text-center w-16 truncate" style={{ fontSize: '10px', color: 'var(--muted-foreground)' }}>
                            {new Date(event.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </div>
                        )}
                      </div>
                      {i < arr.length - 1 && (
                        <div className="flex-1 h-0.5 mx-1 transition-colors" style={{ background: isDone ? 'var(--accent)' : 'var(--border)' }} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Template Selector */}
            <div className="max-w-4xl mx-auto bg-white border rounded-xl p-5 mb-6" style={{ borderColor: 'var(--border)' }}>
              <h3 className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--muted-foreground)' }}>
                🎨 Client Quote Template
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
                {[
                  { id: 'modern', emoji: '✨', name: 'Modern', subtitle: 'Clean & Professional' },
                  { id: 'magical', emoji: '🏰', name: 'Magical', subtitle: 'Disney & Theme Parks' },
                  { id: 'modern_agency', emoji: '🏢', name: 'Modern Agency', subtitle: 'Corporate Timeline' },
                  { id: 'editorial_magazine', emoji: '📖', name: 'Editorial', subtitle: 'Luxury Magazine' },
                  { id: 'expedition_adventure', emoji: '⛰️', name: 'Expedition', subtitle: 'Adventure Trail' },
                  { id: 'executive_brief', emoji: '📋', name: 'Executive Brief', subtitle: 'Corporate Minimal' },
                  { id: 'studio_action', emoji: '🎬', name: 'Studio Action', subtitle: 'Universal Studios' },
                  { id: 'luxury_river', emoji: '⚓', name: 'Luxury River', subtitle: 'Cruise Elegance' },
                  { id: 'tropical_escape', emoji: '🏝️', name: 'Tropical Escape', subtitle: 'Beach & Resort' },
                  { id: 'boarding_pass', emoji: '✈️', name: 'Boarding Pass', subtitle: 'Flight Only' },
                  { id: 'destination_wedding', emoji: '💒', name: 'Wedding', subtitle: 'Destination Romance' },
                ].map(tpl => (
                  <button
                    key={tpl.id}
                    type="button"
                    onClick={() => setSelectedTemplate(tpl.id)}
                    className="p-3 rounded-lg border-2 text-center transition-all"
                    style={selectedTemplate === tpl.id
                      ? { borderColor: 'var(--accent)', background: 'rgba(196, 135, 31, 0.08)' }
                      : { borderColor: 'var(--border)', background: 'white' }}
                  >
                    <div className="text-xl mb-1">{tpl.emoji}</div>
                    <div className="text-xs font-semibold" style={{ color: 'var(--foreground)' }}>{tpl.name}</div>
                    <div className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{tpl.subtitle}</div>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Quote Summary */}
            <div className="quote-summary">
              <div className="quote-header">
                <h3>{quote.quoteTitle}</h3>
                <div className="quote-details">
                  <p><strong>Client:</strong> {quote.client?.firstName} {quote.client?.lastName}</p>
                  <p><strong>Email:</strong> {quote.client?.email}</p>
                  <p><strong>Travel Dates:</strong> {quote.travelDates.start} to {quote.travelDates.end}</p>
                  <p><strong>Travelers:</strong> {quote.travelers.adults} adults{quote.travelers.children.length > 0 && `, ${quote.travelers.children.length} children`}</p>
                  {quote.expiryDate && (
                    <p><strong>Quote Valid Until:</strong> {new Date(quote.expiryDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                  )}
                </div>
              </div>

              {/* Components List */}
              <div className="components-preview">
                <h4>Trip Components</h4>
                {(() => {
                  const clientSelections = quote.options?.client_selections || {};
                  const hasClientSelections = Object.keys(clientSelections).length > 0;
                  
                  return (Array.isArray(quote.components) ? quote.components : []).map(component => {
                    const isSelected = clientSelections[component.id];
                    const isOptional = component.isOption || component.data?.isOption || false;
                    const optionDescription = component.optionDescription || component.data?.optionDescription || '';
                    
                    return (
                      <div key={component.id} className={`component-preview ${isOptional ? 'optional-component' : 'required-component'} ${hasClientSelections && isOptional ? (isSelected ? 'client-selected' : 'client-not-selected') : ''}`}>
                        <div className="component-header">
                          <div className="component-title-section">
                            <span>{componentTypes.find(t => t.id === component.type)?.icon} {component.title}</span>
                            {isOptional && (
                              <div className="component-status-badges">
                                <span className="optional-badge">Optional</span>
                                {hasClientSelections && (
                                  <span className={`selection-badge ${isSelected ? 'selected' : 'not-selected'}`}>
                                    {isSelected ? '✓ Client Selected' : '✗ Not Selected'}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          <span className="component-price">${(parseFloat(component.price) || 0).toFixed(2)}</span>
                        </div>
                        {needsDateReminder(component) && (
                          <p className="needs-dates-warning">
                            🗓️ Client requested <strong>{component.requestedTimeframe}</strong> - exact dates not selected yet
                          </p>
                        )}
                        {component.description && <p>{component.description}</p>}
                        {isOptional && optionDescription && (
                          <p className="option-description">
                            <strong>Client sees:</strong> {optionDescription}
                          </p>
                        )}
                      </div>
                    );
                  });
                })()}
                
                {(() => {
                  const clientSelections = quote.options?.client_selections;
                  if (clientSelections && Object.keys(clientSelections).length > 0) {
                    const selectionTimestamp = quote.options?.selection_timestamp;
                    return (
                      <div className="client-selections-summary">
                        <h5>📋 Client Selection Summary</h5>
                        <p>
                          <strong>Last updated:</strong> {selectionTimestamp ? new Date(selectionTimestamp).toLocaleString() : 'Unknown'}
                        </p>
                        {quote.options?.selected_total && (
                          <div className="selection-totals">
                            <p><strong>Selected Total:</strong> ${quote.options.selected_total.toFixed(2)}</p>
                            <p><strong>Initial Payment Due:</strong> ${quote.options.selected_deposit?.toFixed(2) || '0.00'}</p>
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>

              {/* Pricing Summary */}
              <div className="pricing-summary">
                <div className="total-price">
                  <strong>Total: ${(quote.totalPrice || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</strong>
                </div>
              </div>
            </div>
            <div className="agent-edit-sections">
              <div className="deposit-section">
                <h3>💰 Deposit Information</h3>
                <div className="deposit-options">
                  <div className="form-group">
                    <label>Deposit Amount ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={quote.depositAmount || ''}
                      onChange={(e) => {
                        setDepositManuallyOverridden(true);
                        setQuote(prev => ({ 
                          ...prev, 
                          depositAmount: parseFloat(e.target.value) || 0
                        }));
                      }}
                    />
                    <small style={{color: 'var(--muted-foreground)', fontSize: '0.85em', marginTop: '4px', display: 'block'}}>
                      {depositManuallyOverridden ? (
                        <>
                          🔒 <strong>Manual Override Active</strong> - Deposit will not auto-update when components change.
                          <button
                            type="button"
                            onClick={() => {
                              setDepositManuallyOverridden(false);
                              setQuote(prev => ({ 
                                ...prev, 
                                depositAmount: calculateTotalDeposit(prev.components)
                              }));
                            }}
                            style={{
                              marginLeft: '8px',
                              padding: '2px 8px',
                              fontSize: '0.85em',
                              background: 'var(--accent)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            🔄 Reset to Auto-Calculate
                          </button>
                        </>
                      ) : (
                        '💡 Auto-calculated from component payment timing settings. You can override if needed.'
                      )}
                    </small>
                  </div>
                  <div className="form-group">
                    <label>Deposit Due Date</label>
                    <input
                      type="date"
                      value={quote.depositDueDate}
                      onChange={(e) => setQuote(prev => ({ 
                        ...prev, 
                        depositDueDate: e.target.value
                      }))}
                    />
                  </div>
                </div>
              </div>

              <div className="notes-section">
                <label>Additional Notes</label>
                <textarea
                  value={quote.notes}
                  onChange={(e) => setQuote(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any additional information for the client..."
                  rows="4"
                />
              </div>
            </div>

            {/* Live Template Preview */}
            <div style={{
              marginTop: '40px',
              marginBottom: '30px',
              padding: '20px',
              background: 'white',
              borderRadius: '12px',
              border: '2px solid #e2e8f0',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1e293b',
                marginBottom: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                👁️ Live Preview - {
                  selectedTemplate === 'modern' ? 'Modern' :
                  selectedTemplate === 'magical' ? 'Magical' :
                  selectedTemplate === 'modern_agency' ? 'Modern Agency' :
                  selectedTemplate === 'editorial_magazine' ? 'Editorial Magazine' :
                  selectedTemplate === 'expedition_adventure' ? 'Expedition Adventure' :
                  selectedTemplate === 'executive_brief' ? 'Executive Brief' :
                  selectedTemplate === 'studio_action' ? 'Studio Action' :
                  selectedTemplate === 'luxury_river' ? 'Luxury River' :
                  selectedTemplate === 'tropical_escape' ? 'Tropical Escape' :
                  selectedTemplate === 'boarding_pass' ? 'Boarding Pass' :
                  selectedTemplate === 'destination_wedding' ? 'Destination Wedding' :
                  'Modern'
                } Template
              </h3>
              <div style={{
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                overflow: 'hidden',
                maxHeight: '600px',
                overflowY: 'auto'
              }}>
                <QuoteRenderer 
                  key={selectedTemplate}
                  quoteData={{
                    quoteTitle: quote.quoteTitle,
                    quoteNumber: 'PREVIEW',
                    client: {
                      firstName: quote.client?.firstName || '',
                      lastName: quote.client?.lastName || '',
                      email: quote.client?.email || '',
                      phone: quote.client?.phone || ''
                    },
                    travelDates: {
                      start: quote.travelDates.start,
                      end: quote.travelDates.end
                    },
                    travelers: {
                      adults: quote.travelers.adults || 1,
                      children: quote.travelers.children || []
                    },
                    components: quote.components || [],
                    totalPrice: quote.totalPrice || 0,
                    depositAmount: quote.depositAmount || 0,
                    depositDueDate: quote.depositDueDate,
                    notes: quote.notes || '',
                    agent_name: user?.name || '',
                    agent_email: user?.email || '',
                    agent_phone: user?.phone || '',
                    agent_business_name: user?.business_name || '',
                    agent_logo_url: user?.logo_url || ''
                  }}
                  templateId={selectedTemplate}
                  isAgentPreview={true}
                />
              </div>
            </div>

            <div className="form-actions preview-actions">
              <button 
                type="button"
                className="btn btn-secondary"
                onClick={() => setActiveStep('components')}
              >
                Back
              </button>
              
              <button 
                type="button"
                className="btn btn-info preview-quote-btn"
                onClick={async () => {
                  try {
                    let currentQuoteId = quote.id || quoteId; // Use quote.id or state quoteId
                    
                    if (!currentQuoteId) {
                      // New quote - must save first, but stay on page
                      const response = await handleSaveQuote(false, true); // Save as draft, stay on page
                      currentQuoteId = response.quoteId || response.id;
                      
                      if (!currentQuoteId) {
                        toast.error('Failed to save quote. Please try again.');
                        return;
                      }
                    } else {
                      // Existing quote - auto-save current changes before preview, stay on page
                      await handleSaveQuote(false, true); // Save as draft, stay on page
                    }
                    
                    // Open preview after successful save
                    const previewUrl = `${basePath}/quotes/${currentQuoteId}/preview`;
                    window.open(previewUrl, '_blank', 'width=1200,height=800');
                  } catch (error) {
                    console.error('Error saving quote before preview:', error);
                    toast.error('Failed to save quote. Please try again.');
                  }
                }}
                disabled={loading || !quote.client || (Array.isArray(quote.components) ? quote.components : []).length === 0}
              >
                {loading ? 'Saving...' : 'Preview Quote'}
              </button>
              
              <button 
                type="button"
                className="btn btn-secondary"
                style={{ display: 'none' }}
                onClick={() => {
                  // Open preview in new window
                  const previewWindow = window.open('', '_blank', 'width=800,height=600');
                  const previewContent = `
                    <!DOCTYPE html>
                    <html>
                    <head>
                      <title>Quote Preview - ${quote.quoteTitle}</title>
                      <style>
                        body { 
                          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
                          max-width: 900px; 
                          margin: 0 auto; 
                          padding: 40px 30px; 
                          background: #f8fafc;
                          color: #334155;
                          line-height: 1.6;
                        }
                        .quote-container {
                          background: white;
                          border-radius: 12px;
                          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
                          overflow: hidden;
                        }
                        .header { 
                          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
                          color: white;
                          padding: 40px 30px;
                          text-align: center;
                        }
                        .header h1 { 
                          margin: 0 0 10px 0; 
                          font-size: 2.5em; 
                          font-weight: 700;
                          text-shadow: 0 2px 4px rgba(0,0,0,0.1);
                        }
                        .header p { 
                          margin: 5px 0; 
                          font-size: 1.1em;
                          opacity: 0.9;
                        }
                        .content {
                          padding: 30px;
                        }
                        .section { 
                          margin-bottom: 35px; 
                          padding-bottom: 25px;
                          border-bottom: 1px solid #e2e8f0;
                        }
                        .section:last-child {
                          border-bottom: none;
                          margin-bottom: 0;
                        }
                        .section h2 { 
                          color: #1e40af; 
                          margin: 0 0 20px 0;
                          font-size: 1.5em;
                          font-weight: 600;
                          display: flex;
                          align-items: center;
                          gap: 10px;
                        }
                        .section h2:before {
                          content: '';
                          width: 4px;
                          height: 24px;
                          background: #2563eb;
                          border-radius: 2px;
                        }
                        .detail-row {
                          display: flex;
                          justify-content: space-between;
                          margin-bottom: 8px;
                          padding: 8px 0;
                        }
                        .detail-label {
                          font-weight: 600;
                          color: #475569;
                        }
                        .detail-value {
                          color: #1e293b;
                        }
                        .component { 
                          background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
                          padding: 20px; 
                          margin-bottom: 15px; 
                          border-radius: 10px;
                          border-left: 4px solid #2563eb;
                          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
                        }
                        .component h3 { 
                          margin: 0 0 10px 0; 
                          color: #1e40af;
                          font-size: 1.2em;
                          font-weight: 600;
                        }
                        .component p { 
                          margin: 8px 0; 
                          color: #475569;
                        }
                        .component-price { 
                          font-weight: 700; 
                          color: #059669;
                          font-size: 1.1em;
                          text-align: right;
                          margin-top: 10px;
                        }
                        .pricing-summary {
                          background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
                          padding: 25px;
                          border-radius: 10px;
                          border: 2px solid #0ea5e9;
                          margin-top: 20px;
                        }
                        .pricing-row {
                          display: flex;
                          justify-content: space-between;
                          align-items: center;
                          margin-bottom: 12px;
                          padding: 8px 0;
                        }
                        .pricing-row.total {
                          border-top: 2px solid #0ea5e9;
                          padding-top: 15px;
                          margin-top: 15px;
                          font-size: 1.3em;
                          font-weight: 700;
                        }
                        .pricing-label {
                          font-weight: 600;
                          color: #0c4a6e;
                        }
                        .pricing-value {
                          font-weight: 700;
                          color: #059669;
                          font-size: 1.1em;
                        }
                        .pricing-row.total .pricing-value {
                          font-size: 1.4em;
                          color: #0c4a6e;
                        }
                        .attachments-section {
                          margin: 15px 0;
                          padding: 10px 0;
                          border-top: 1px solid #e2e8f0;
                        }
                        .attachments-section h4 {
                          color: #1e40af;
                          margin: 15px 0 8px 0;
                          font-size: 1.1em;
                          font-weight: 600;
                        }
                        .attachments-section img {
                          max-width: 200px;
                          max-height: 150px;
                          margin: 5px;
                          border-radius: 5px;
                          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                        }
                        .attachments-section ul {
                          margin: 8px 0;
                          padding-left: 20px;
                        }
                        .attachments-section li {
                          margin: 4px 0;
                        }
                        .footer {
                          text-align: center;
                          padding: 20px;
                          background: #f8fafc;
                          color: #64748b;
                          font-size: 0.9em;
                        }
                        @media print {
                          body { background: white; }
                          .quote-container { box-shadow: none; }
                        }
                      </style>
                    </head>
                    <body>
                      <div class="quote-container">
                        <div class="header">
                          <h1>${quote.quoteTitle}</h1>
                          <p><strong>Prepared for:</strong> ${quote.client?.firstName} ${quote.client?.lastName}</p>
                          <p><strong>Date:</strong> ${new Date().toLocaleDateString()}</p>
                        </div>
                        
                        <div class="content">
                          <div class="section">
                            <h2>Trip Details</h2>
                            <div class="detail-row">
                              <span class="detail-label">Travel Dates:</span>
                              <span class="detail-value">${quote.travelDates.start} to ${quote.travelDates.end}</span>
                            </div>
                            <div class="detail-row">
                              <span class="detail-label">Travelers:</span>
                              <span class="detail-value">${quote.travelers.adults} Adult${quote.travelers.adults !== 1 ? 's' : ''}${quote.travelers.children.length > 0 ? `, ${quote.travelers.children.length} Child${quote.travelers.children.length !== 1 ? 'ren' : ''}` : ''}</span>
                            </div>
                          </div>
                          
                          <div class="section">
                            <h2>Travel Components</h2>
                            ${(Array.isArray(quote.components) ? quote.components : [])
                              .filter(comp => comp && comp.title && comp.title !== 'undefined' && comp.title.trim() !== '')
                              .map(comp => {
                                // Helper function to render component dates
                                const renderDates = (component) => {
                                  let dateInfo = '';
                                  
                                  // Flight dates
                                  if (component.departureDate) {
                                    dateInfo += `<p><strong>${component.tripType === 'round_trip' ? 'Outbound Departure' : 'Departure'}:</strong> ${component.departureDate}${component.departureTime ? ` at ${component.departureTime}` : ''}</p>`;
                                  }
                                  if (component.arrivalDate) {
                                    dateInfo += `<p><strong>${component.tripType === 'round_trip' ? 'Outbound Arrival' : 'Arrival'}:</strong> ${component.arrivalDate}${component.arrivalTime ? ` at ${component.arrivalTime}` : ''}</p>`;
                                  }
                                  if (component.tripType === 'round_trip' && component.returnDepartureDate) {
                                    dateInfo += `<p><strong>Return Departure:</strong> ${component.returnDepartureDate}${component.returnDepartureTime ? ` at ${component.returnDepartureTime}` : ''}</p>`;
                                  }
                                  if (component.tripType === 'round_trip' && component.returnArrivalDate) {
                                    dateInfo += `<p><strong>Return Arrival:</strong> ${component.returnArrivalDate}${component.returnArrivalTime ? ` at ${component.returnArrivalTime}` : ''}</p>`;
                                  }
                                  
                                  // Hotel dates
                                  if (component.checkInDate) {
                                    dateInfo += `<p><strong>Check-in:</strong> ${component.checkInDate}</p>`;
                                  }
                                  if (component.checkOutDate) {
                                    dateInfo += `<p><strong>Check-out:</strong> ${component.checkOutDate}</p>`;
                                  }
                                  
                                  // Car rental dates
                                  if (component.pickupDate) {
                                    dateInfo += `<p><strong>Pickup:</strong> ${component.pickupDate}${component.pickupTime ? ` at ${component.pickupTime}` : ''}</p>`;
                                  }
                                  if (component.dropoffDate) {
                                    dateInfo += `<p><strong>Drop-off:</strong> ${component.dropoffDate}${component.dropoffTime ? ` at ${component.dropoffTime}` : ''}</p>`;
                                  }
                                  
                                  // Insurance policy dates
                                  if (component.policyStartDate) {
                                    dateInfo += `<p><strong>Policy Start:</strong> ${component.policyStartDate}</p>`;
                                  }
                                  if (component.policyEndDate) {
                                    dateInfo += `<p><strong>Policy End:</strong> ${component.policyEndDate}</p>`;
                                  }
                                  
                                  // Activity/Restaurant/General dates
                                  if (component.date) {
                                    dateInfo += `<p><strong>Date:</strong> ${component.date}${component.time ? ` at ${component.time}` : ''}</p>`;
                                  }
                                  
                                  return dateInfo;
                                };
                                
                                // Helper function to render component details
                                const renderDetails = (component) => {
                                  let details = '';
                                  
                                  // Flight details
                                  if (component.airline) {
                                    details += `<p><strong>Airline:</strong> ${component.airline}${component.flightNumber ? ` (${component.flightNumber})` : ''}</p>`;
                                  }
                                  if (component.departureAirport || component.arrivalAirport) {
                                    details += `<p><strong>${component.tripType === 'round_trip' ? 'Outbound Route' : 'Route'}:</strong> ${component.departureAirport || 'N/A'} → ${component.arrivalAirport || 'N/A'}</p>`;
                                  }
                                  if (component.seats) {
                                    details += `<p><strong>${component.tripType === 'round_trip' ? 'Outbound Seats' : 'Seats'}:</strong> ${component.seats}</p>`;
                                  }
                                  if (component.confirmationNumber) {
                                    details += `<p><strong>${component.tripType === 'round_trip' ? 'Outbound Confirmation Number' : 'Confirmation Number'}:</strong> ${component.confirmationNumber}</p>`;
                                  }
                                  if (component.tripType === 'round_trip' && (component.returnDepartureAirport || component.returnArrivalAirport)) {
                                    const returnAirlineText = component.returnAirline || component.returnFlightNumber
                                      ? ` (${[component.returnAirline || component.airline, component.returnFlightNumber].filter(Boolean).join(' ')})`
                                      : '';
                                    details += `<p><strong>Return Route:</strong> ${component.returnDepartureAirport || 'N/A'} → ${component.returnArrivalAirport || 'N/A'}${returnAirlineText}</p>`;
                                  }
                                  if (component.tripType === 'round_trip' && component.returnConfirmationNumber) {
                                    details += `<p><strong>Return Confirmation Number:</strong> ${component.returnConfirmationNumber}</p>`;
                                  }
                                  if (component.tripType === 'round_trip' && component.returnSeats) {
                                    details += `<p><strong>Return Seats:</strong> ${component.returnSeats}</p>`;
                                  }
                                  
                                  // Hotel details
                                  if (component.propertyName) {
                                    details += `<p><strong>Property:</strong> ${component.propertyName}</p>`;
                                  }
                                  if (component.location) {
                                    details += `<p><strong>Location:</strong> ${component.location}</p>`;
                                  }
                                  if (component.roomType) {
                                    details += `<p><strong>Room Type:</strong> ${component.roomType}</p>`;
                                  }
                                  
                                  // Restaurant details
                                  if (component.restaurantName) {
                                    details += `<p><strong>Restaurant:</strong> ${component.restaurantName}</p>`;
                                  }
                                  if (component.partySize) {
                                    details += `<p><strong>Party Size:</strong> ${component.partySize}</p>`;
                                  }
                                  
                                  // Activity details
                                  if (component.activityName) {
                                    details += `<p><strong>Activity:</strong> ${component.activityName}</p>`;
                                  }
                                  if (component.address) {
                                    details += `<p><strong>Address:</strong> ${component.address}</p>`;
                                  }
                                  
                                  return details;
                                };
                                
                                // Helper function to render attachments
                                const renderAttachments = (component) => {
                                  let attachmentHtml = '';
                                  
                                  if (component.photos && component.photos.length > 0) {
                                    attachmentHtml += '<div class="attachments-section"><h4>Photos:</h4>';
                                    component.photos.forEach(photo => {
                                      if (photo.url) {
                                        attachmentHtml += `<img src="${photo.url}" alt="${photo.name || 'Photo'}" style="max-width: 200px; max-height: 150px; margin: 5px; border-radius: 5px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">`;
                                      }
                                    });
                                    attachmentHtml += '</div>';
                                  }
                                  
                                  if (component.documents && component.documents.length > 0) {
                                    attachmentHtml += '<div class="attachments-section"><h4>Documents:</h4><ul>';
                                    component.documents.forEach(doc => {
                                      if (doc.url && doc.name) {
                                        attachmentHtml += `<li><a href="${doc.url}" target="_blank" style="color: #2563eb; text-decoration: none;">${doc.name}</a></li>`;
                                      }
                                    });
                                    attachmentHtml += '</ul></div>';
                                  }
                                  
                                  return attachmentHtml;
                                };
                                
                                // Actual field values (departureDate, airline,
                                // etc.) live on comp.data, not comp itself -
                                // renderDates/renderDetails read comp.data.
                                return `
                                  <div class="component">
                                    <h3>${comp.title}</h3>
                                    ${comp.description ? `<p><strong>Description:</strong> ${comp.description}</p>` : ''}
                                    ${renderDates(comp.data || {})}
                                    ${renderDetails(comp.data || {})}
                                    ${renderAttachments(comp)}
                                    <div class="component-price">$${(parseFloat(comp.price) || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                                  </div>
                                `;
                              }).join('')}
                          </div>
                          
                          <div class="pricing-summary">
                            <div class="pricing-row total">
                              <span class="pricing-label">Total Price:</span>
                              <span class="pricing-value">$${(quote.totalPrice || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                            </div>
                            ${quote.depositAmount ? `
                              <div class="pricing-row">
                                <span class="pricing-label">Deposit Required:</span>
                                <span class="pricing-value">$${(quote.depositAmount || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                              </div>
                            ` : ''}
                          </div>
                        </div>
                        
                        <div class="footer">
                          ${quote.agentInfo?.termsConditions ? `<p>${quote.agentInfo.termsConditions}</p>` : '<p>This quote is valid for 30 days from the date of issue.</p>'}
                        </div>
                      </div>
                    </body>
                    </html>
                  `;
                  previewWindow.document.write(previewContent);
                  previewWindow.document.close();
                }}
                disabled={!quote.client || (Array.isArray(quote.components) ? quote.components : []).length === 0}
              >
                Preview Quote
              </button>

              {isEditMode ? (
                <button 
                  className="btn btn-secondary"
                  onClick={() => handleSaveQuote(false)}
                  disabled={loading}
                >
                  Save Changes
                </button>
              ) : (
                <button 
                  className="btn btn-secondary"
                  onClick={() => handleSaveQuote(false)}
                  disabled={loading}
                >
                  Save Draft
                </button>
              )}

              <button 
                className="btn btn-primary"
                onClick={() => {
                  // For multi-option AI quotes, ask the agent whether to send
                  // the comparison link (all options) or just the active one.
                  if (quoteOptionGroup.length > 1) {
                    setShowSendModal(true);
                  } else {
                    handleSaveQuote(true, false, 'current');
                  }
                }}
                disabled={loading || !quote.client || (Array.isArray(quote.components) ? quote.components : []).length === 0}
              >
                {loading ? 'Sending...' : 'Send To Client'}
              </button>
            </div>
          </div>
        )}

        {/* Send Quote Step */}
        {activeStep === 'send' && (
          <div className="step-content">
            <h2>Send Quote</h2>
            <p>Quote sending functionality would go here...</p>
          </div>
        )}
      </div>

      {/* Persistent bottom bar - Back / progress dots / Continue (Figma
          "Voyager Pro" pattern). Step 4 (Review & Send) handles its own
          Save Draft/Send To Client actions inline above, so this just shows
          Back + dots there. */}
      {activeStep !== 'send' && (
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-t flex-shrink-0" style={{ borderColor: 'var(--border)', background: 'rgba(255,255,255,0.6)' }}>
          <button
            type="button"
            onClick={() => {
              if (activeStepIndex > 0) setActiveStep(QUOTE_BUILDER_STEPS[activeStepIndex - 1].id);
            }}
            disabled={activeStepIndex === 0}
            className="flex items-center gap-1.5 sm:gap-2 px-4 sm:px-5 py-2.5 border rounded-xl text-sm font-medium transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            style={{ borderColor: 'var(--border)', color: 'var(--foreground)' }}
          >
            <ChevronRight size={14} style={{ transform: 'rotate(180deg)' }} />
            Back
          </button>
          <div className="flex gap-1.5">
            {QUOTE_BUILDER_STEPS.map((s, i) => (
              <div
                key={s.id}
                className="rounded-full transition-all duration-300"
                style={i === activeStepIndex
                  ? { width: 20, height: 8, background: 'var(--accent)' }
                  : i < activeStepIndex
                    ? { width: 8, height: 8, background: 'rgba(196, 135, 31, 0.4)' }
                    : { width: 8, height: 8, background: 'var(--border)' }}
              />
            ))}
          </div>
          {activeStepIndex < QUOTE_BUILDER_STEPS.length - 1 ? (
            <button
              type="button"
              onClick={() => {
                if (!isStepReady(activeStep)) return;
                // Trip Details specifically needs autosave kicked off the
                // moment it's complete (see detailsStepCompletedRef comment
                // above) rather than waiting for the agent to touch
                // something else afterward.
                if (activeStep === 'details') {
                  detailsStepCompletedRef.current = true;
                  scheduleAutosave();
                }
                setActiveStep(QUOTE_BUILDER_STEPS[activeStepIndex + 1].id);
              }}
              disabled={!isStepReady(activeStep)}
              className="flex items-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2.5 text-white rounded-xl text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: 'var(--primary)' }}
            >
              Continue
              <ChevronRight size={14} />
            </button>
          ) : (
            <div className="w-20 sm:w-24" />
          )}
        </div>
      )}

      {/* Send Quote Modal - choose Comparison vs Current Option for multi-option AI quotes */}
      <Modal
        isOpen={showSendModal}
        onClose={() => setShowSendModal(false)}
        title="Send Quote to Client"
        maxWidth="480px"
      >
            <div className="send-quote-modal-body">
              <p>This quote has {quoteOptionGroup.length} package options. How would you like to send it?</p>

              <button
                type="button"
                className="send-quote-option-btn"
                onClick={() => {
                  setShowSendModal(false);
                  handleSaveQuote(true, false, 'comparison');
                }}
                disabled={loading}
              >
                <span className="send-quote-option-title">📊 Send Comparison (All Options)</span>
                <span className="send-quote-option-desc">
                  Client gets one link to a tabbed page comparing all {quoteOptionGroup.length} packages side by side.
                </span>
              </button>

              <button
                type="button"
                className="send-quote-option-btn"
                onClick={() => {
                  setShowSendModal(false);
                  handleSaveQuote(true, false, 'current');
                }}
                disabled={loading}
              >
                <span className="send-quote-option-title">📄 Send Current Option Only</span>
                <span className="send-quote-option-desc">
                  Client gets a link to just "{quoteOptionGroup[activeOptionIndex]?.optionName || 'this option'}" — no comparison.
                </span>
              </button>
            </div>
      </Modal>

      {/* PDF Upload Modal */}
      {showPDFUploadModal && (
        <PDFQuoteUploadModal
          onClose={() => setShowPDFUploadModal(false)}
          onQuoteDataExtracted={(data) => {
            setPdfExtractedData(data);
            setShowPDFReviewModal(true);
          }}
        />
      )}

      {/* PDF Review Modal */}
      {showPDFReviewModal && pdfExtractedData && (
        <PDFQuoteReviewModal
          extractedData={pdfExtractedData}
          onConfirm={(components, metadata) => {
            // Track package group ID for related components
            const packageGroupId = Date.now().toString();
            let mainComponentTitle = '';

            // Add components to quote with relationship tracking
            components.forEach((comp, index) => {
              // Handle bundled pricing - set to 0 if included in package
              let componentPrice = 0;
              
              // For main component, use offer subtotal if available
              if (comp.isMainComponent && pdfExtractedData.offerSubtotal) {
                componentPrice = pdfExtractedData.offerSubtotal;
              } else if (comp.includedInPackage) {
                componentPrice = 0; // Will show as "Included in Package Price"
              } else if (comp.data.price !== null && comp.data.price !== undefined) {
                componentPrice = comp.data.price;
              }

              const componentData = {
                type: comp.type,
                title: comp.data.title || comp.typeName,
                price: componentPrice,
                ...comp.data,
                // Preserve AI extraction metadata
                aiExtracted: true,
                includedInPackage: comp.includedInPackage || false,
                packageGroupId: comp.linkedTo ? packageGroupId : null,
                isMainComponent: comp.isMainComponent || false,
                linkedTo: comp.linkedTo || null,
                relationshipType: comp.relationshipType || null,
                extractionReason: comp.reason || null
              };

              // Track main component title for description enhancement
              if (comp.isMainComponent) {
                mainComponentTitle = componentData.title;
              }

              // For linked components, enhance description with relationship info
              if (comp.linkedTo && comp.relationshipType) {
                const relationshipLabel = {
                  'insurance': '🛡️ Travel Protection',
                  'dining': '🍽️ Dining Plan',
                  'addon': '➕ Add-on Service',
                  'transportation': '🚗 Transportation',
                  'related': '🔗 Related Service'
                }[comp.relationshipType] || '🔗 Related Service';

                componentData.description = componentData.description 
                  ? `${relationshipLabel} - ${componentData.description}`
                  : relationshipLabel;
              }

              handleAddComponent(componentData);
            });

            // Add package extras as note to main component if present
            if (pdfExtractedData.packageExtras && pdfExtractedData.packageExtras.length > 0) {
              const extrasNote = '\n\n📦 Package Extras:\n' + 
                pdfExtractedData.packageExtras.map(extra => 
                  `• ${extra.name}${extra.included ? ' (Included)' : ` - $${extra.price?.toFixed(2) || '0.00'}`}${extra.description ? ': ' + extra.description : ''}`
                ).join('\n');

              // Update the main component's notes with extras info
              setQuote(prev => {
                const updatedComponents = prev.components.map(comp => {
                  if (comp.aiExtracted && comp.isMainComponent && comp.packageGroupId === packageGroupId) {
                    return {
                      ...comp,
                      data: {
                        ...comp.data,
                        special_requests: (comp.data.special_requests || '') + extrasNote
                      }
                    };
                  }
                  return comp;
                });
                return { ...prev, components: updatedComponents };
              });
            }

            // Update quote metadata if available
            if (metadata) {
              setQuote(prev => ({
                ...prev,
                quoteTitle: metadata.title || prev.quoteTitle,
                travelDates: {
                  start: metadata.travelStartDate || prev.travelDates.start,
                  end: metadata.travelEndDate || prev.travelDates.end
                },
                travelers: {
                  adults: metadata.adults || prev.travelers.adults,
                  children: metadata.children || prev.travelers.children
                },
                depositAmount: metadata.depositAmount || prev.depositAmount,
                depositDueDate: metadata.depositDueDate || prev.depositDueDate
              }));
            }

            setShowPDFReviewModal(false);
            setPdfExtractedData(null);
          }}
          onCancel={() => {
            setShowPDFReviewModal(false);
            setPdfExtractedData(null);
          }}
        />
      )}

      {/* Conversational Quote Builder */}
      {showConversationalBuilder && (
        <ConversationalQuoteBuilder
          onClose={() => setShowConversationalBuilder(false)}
          onQuoteCreated={(quoteData) => {
            // Handle quote creation from conversational builder
            setShowConversationalBuilder(false);
            // Future: populate quote with conversational data
          }}
          onFinalizeSuccess={handleAIQuoteFinalized}
        />
      )}

      {/* Component Library Modal */}
      <ComponentLibraryModal
        isOpen={showLibraryModal}
        onClose={() => setShowLibraryModal(false)}
        onSelectTemplate={handleSelectTemplate}
      />

      {/* Client Selection Modal */}
      <ClientModal
        isOpen={showClientModal}
        onClose={() => setShowClientModal(false)}
        clients={clients}
        onSelectClient={handleClientSelect}
        onCreateClient={handleNewClient}
      />

      {/* Error display */}
      {errors.submit && (
        <div className="alert alert-error">
          {errors.submit}
        </div>
      )}
    </div>
  );
};

export default QuoteBuilder;
